import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import JSZip from "jszip";
import { readPsd } from "ag-psd";
import { pdfjs } from "react-pdf";
import { apiClient } from "../api/client";
import { PdfViewer } from "./PdfViewer";
import { getFFprobe } from "../utils/ffmpegLoader";
import "./VideoComparisonModal.css";

type ComparisonFileType =
  | "video"
  | "audio"
  | "pdf"
  | "image"
  | "docx"
  | "text"
  | "other";

interface FileTypeConfig {
  extensions: string[];
  mimeType: string;
  type: ComparisonFileType;
}

const FILE_TYPE_CONFIGS: FileTypeConfig[] = [
  { extensions: [".mp4", ".m4v"], mimeType: "video/mp4", type: "video" },
  { extensions: [".webm"], mimeType: "video/webm", type: "video" },
  { extensions: [".mov"], mimeType: "video/quicktime", type: "video" },
  { extensions: [".avi"], mimeType: "video/x-msvideo", type: "video" },
  { extensions: [".mkv"], mimeType: "video/x-matroska", type: "video" },
  { extensions: [".flv"], mimeType: "video/x-flv", type: "video" },
  { extensions: [".wmv"], mimeType: "video/x-ms-wmv", type: "video" },
  { extensions: [".mp3"], mimeType: "audio/mpeg", type: "audio" },
  { extensions: [".wav"], mimeType: "audio/wav", type: "audio" },
  { extensions: [".ogg"], mimeType: "audio/ogg", type: "audio" },
  { extensions: [".m4a"], mimeType: "audio/mp4", type: "audio" },
  { extensions: [".flac"], mimeType: "audio/flac", type: "audio" },
  { extensions: [".aac"], mimeType: "audio/aac", type: "audio" },
  { extensions: [".pdf"], mimeType: "application/pdf", type: "pdf" },
  { extensions: [".png"], mimeType: "image/png", type: "image" },
  { extensions: [".jpg", ".jpeg"], mimeType: "image/jpeg", type: "image" },
  { extensions: [".gif"], mimeType: "image/gif", type: "image" },
  { extensions: [".bmp"], mimeType: "image/bmp", type: "image" },
  { extensions: [".svg"], mimeType: "image/svg+xml", type: "image" },
  { extensions: [".xcf"], mimeType: "image/x-xcf", type: "image" },
  { extensions: [".psd"], mimeType: "image/vnd.adobe.photoshop", type: "image" },
  {
    extensions: [".txt", ".csv", ".json", ".md", ".log"],
    mimeType: "text/plain",
    type: "text",
  },
  { extensions: [".html", ".htm"], mimeType: "text/html", type: "text" },
  {
    extensions: [".docx"],
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    type: "docx",
  },
  {
    extensions: [".odt"],
    mimeType: "application/vnd.oasis.opendocument.text",
    type: "docx",
  },
  {
    extensions: [".ods"],
    mimeType: "application/vnd.oasis.opendocument.spreadsheet",
    type: "docx",
  },
  {
    extensions: [".odp"],
    mimeType: "application/vnd.oasis.opendocument.presentation",
    type: "docx",
  },
  {
    extensions: [".xlsx"],
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    type: "docx",
  },
  {
    extensions: [".pptx"],
    mimeType:
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    type: "docx",
  },
];

const EXTENSION_CONFIG_MAP = FILE_TYPE_CONFIGS.reduce<
  Map<string, FileTypeConfig>
>((map, config) => {
  for (const ext of config.extensions) {
    map.set(ext, config);
  }
  return map;
}, new Map());

const SIDE_BY_SIDE_MAX_WIDTH = 2500;
const SIDE_BY_SIDE_MAX_HEIGHT = 3000;
const CONTENT_HORIZONTAL_PADDING = 48; // 24px padding on each side of modal content

const getFileExtension = (filename: string): string => {
  const lastDot = filename.lastIndexOf(".");
  return lastDot >= 0 ? filename.substring(lastDot).toLowerCase() : "";
};

const getFileTypeInfo = (filename: string) => {
  const extension = getFileExtension(filename);
  const config = EXTENSION_CONFIG_MAP.get(extension);
  if (config) {
    return { ...config, extension };
  }
  return {
    extensions: [extension],
    mimeType: "application/octet-stream",
    type: "other" as ComparisonFileType,
    extension,
  };
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const renderPsdToBlob = async (psdBlob: Blob): Promise<Blob> => {
  try {
    const arrayBuffer = await psdBlob.arrayBuffer();
    const psd = readPsd(arrayBuffer);

    if (!psd.width || !psd.height) {
      throw new Error("Invalid PSD: missing dimensions");
    }

    const canvas = document.createElement("canvas");
    canvas.width = psd.width;
    canvas.height = psd.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to get canvas context");
    }

    // Draw the composite image if available
    if (psd.canvas) {
      ctx.drawImage(psd.canvas, 0, 0);
    } else if (psd.imageData) {
      // Convert PixelData to ImageData
      const imageData = new ImageData(
        new Uint8ClampedArray(psd.imageData.data),
        psd.imageData.width,
        psd.imageData.height
      );
      ctx.putImageData(imageData, 0, 0);
    } else {
      throw new Error("PSD has no renderable image data");
    }

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to convert canvas to blob"));
        }
      }, "image/png");
    });
  } catch (error) {
    console.error("Failed to render PSD:", error);
    throw error;
  }
};

interface VideoComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  problemRunId?: string;
  // Optional props for label-platform compatibility
  problemId?: string;
  projectId?: string | null;
  isTutorial?: boolean;
}

interface HoverCoords {
  x: number;
  y: number;
  offsetX: number;
  offsetY: number;
  rectWidth: number;
  rectHeight: number;
}

interface ApiVideoFileDetails {
  filename: string;
  url?: string | null; // Presigned URL for client-side extraction
  frame_count?: number | null;
  duration_seconds?: number | null;
  frame_rate?: number | null;
  width?: number | null;
  height?: number | null;
  codec?: string | null;
  size_bytes?: number | null;
  mime_type?: string | null;
  analysis_error?: string | null;
  sample_rate?: number | null;
  sample_count?: number | null;
  channels?: number | null;
  bit_rate?: number | null;
}

interface ApiVideoComparisonPair {
  filename: string;
  solution: ApiVideoFileDetails;
  ai_output: ApiVideoFileDetails;
}

interface VideoPair {
  filename: string;
  solutionFilename: string;
  fileType: ComparisonFileType;
  solutionBlob: Blob;
  aiOutputBlob: Blob;
  metadata?: ApiVideoComparisonPair;
  // Optional diff blob for side-by-side previews with 3 files (left, right, diff)
  diffBlob?: Blob;
}

/**
 * Extract metadata using ffprobe-wasm (accurate and fast).
 * Returns null if extraction fails.
 */
const extractMetadataWithFFprobe = async (
  blob: Blob,
  filename: string,
  ffprobe: any, // FFprobeWorker instance (already loaded)
): Promise<ApiVideoFileDetails | null> => {
  try {
    // ffprobe-wasm expects a File, so convert Blob to File
    const file = new File([blob], filename, { type: blob.type });

    // Get metadata from ffprobe
    const probeData = await ffprobe.getFileInfo(file);

    console.log(`[extractMetadataWithFFprobe] Raw ffprobe data for ${filename}:`, probeData);

    // Parse ffprobe output (same format as native ffprobe JSON)
    const videoStream = probeData.streams?.find(
      (s: any) => s.codec_type === "video"
    );
    const audioStream = probeData.streams?.find(
      (s: any) => s.codec_type === "audio"
    );

    const parsed: ApiVideoFileDetails = {
      filename,
      size_bytes: blob.size,
      mime_type: blob.type || null,
    };

    if (videoStream) {
      parsed.width = videoStream.width || null;
      parsed.height = videoStream.height || null;
      parsed.codec = videoStream.codec_name || null;

      // Parse frame rate (can be "30/1" or "30000/1001")
      if (videoStream.r_frame_rate) {
        const [num, den] = videoStream.r_frame_rate.split("/").map(Number);
        if (num && den) {
          parsed.frame_rate = num / den;
        }
      }

      // Get duration and calculate frame count
      const duration = parseFloat(
        videoStream.duration || probeData.format?.duration || "0"
      );
      if (duration > 0) {
        parsed.duration_seconds = duration;
        if (parsed.frame_rate) {
          parsed.frame_count = Math.round(duration * parsed.frame_rate);
        }
      }
    }

    if (audioStream) {
      // For audio-only files, set codec from audio stream
      if (!videoStream && audioStream.codec_name) {
        parsed.codec = audioStream.codec_name;
      }

      parsed.sample_rate = audioStream.sample_rate
        ? parseInt(audioStream.sample_rate)
        : null;
      parsed.channels = audioStream.channels || null;

      // Try audio stream bit_rate first, then format bit_rate
      parsed.bit_rate = audioStream.bit_rate
        ? parseInt(audioStream.bit_rate)
        : probeData.format?.bit_rate
        ? parseInt(probeData.format.bit_rate)
        : null;

      if (!parsed.duration_seconds) {
        const duration = parseFloat(
          audioStream.duration || probeData.format?.duration || "0"
        );
        if (duration > 0) {
          parsed.duration_seconds = duration;
        }
      }

      if (parsed.sample_rate && parsed.duration_seconds) {
        parsed.sample_count = Math.round(
          parsed.sample_rate * parsed.duration_seconds
        );
      }
    }

    console.log(`[extractMetadataWithFFprobe] Extracted for ${filename}:`, parsed);
    return parsed;
  } catch (error) {
    console.error(`[extractMetadataWithFFprobe] Failed for ${filename}:`, error);
    return null;
  }
};

/**
 * Parse WAV file manually to support formats like 64-bit float PCM that browsers don't natively support.
 * Returns null if not a WAV file or if parsing fails.
 */
const parseWavFile = (arrayBuffer: ArrayBuffer): {
  channels: Float32Array[];
  sampleRate: number;
  duration: number;
} | null => {
  console.log('[parseWavFile] ENTRY - size:', arrayBuffer.byteLength);
  try {
    const view = new DataView(arrayBuffer);

    // Check RIFF header
    const riffHeader = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
    console.log('[parseWavFile] RIFF header:', riffHeader);
    if (riffHeader !== 'RIFF') {
      console.log('[parseWavFile] Not a RIFF file, returning null');
      return null;
    }

    // Check WAVE format
    const waveFormat = String.fromCharCode(view.getUint8(8), view.getUint8(9), view.getUint8(10), view.getUint8(11));
    console.log('[parseWavFile] WAVE format:', waveFormat);
    if (waveFormat !== 'WAVE') {
      console.log('[parseWavFile] Not a WAVE file, returning null');
      return null;
    }

    // Find fmt chunk
    let offset = 12;
    let fmtChunkFound = false;
    let audioFormat = 0;
    let numChannels = 0;
    let sampleRate = 0;
    let bitsPerSample = 0;

    console.log('[parseWavFile] Starting chunk search from offset 12');
    while (offset < view.byteLength - 8) {
      const chunkId = String.fromCharCode(view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3));
      const chunkSize = view.getUint32(offset + 4, true);
      console.log('[parseWavFile] Found chunk:', chunkId, 'size:', chunkSize, 'at offset:', offset);

      if (chunkId === 'fmt ') {
        audioFormat = view.getUint16(offset + 8, true);
        numChannels = view.getUint16(offset + 10, true);
        sampleRate = view.getUint32(offset + 12, true);
        bitsPerSample = view.getUint16(offset + 22, true);
        fmtChunkFound = true;
        console.log('[parseWavFile] Found fmt chunk - audioFormat:', audioFormat, 'channels:', numChannels, 'sampleRate:', sampleRate, 'bitsPerSample:', bitsPerSample);
      } else if (chunkId === 'data' && fmtChunkFound) {
        console.log('[parseWavFile] Found data chunk, starting decode...');
        // Found data chunk, now decode based on format
        const dataOffset = offset + 8;
        const dataSize = chunkSize;

        // Support both PCM (audioFormat = 1) and IEEE Float (audioFormat = 3)
        if (audioFormat !== 1 && audioFormat !== 3) {
          console.log('[parseWavFile] Unsupported audio format:', audioFormat);
          return null;
        }

        const bytesPerSample = bitsPerSample / 8;
        const numSamples = Math.floor(dataSize / (numChannels * bytesPerSample));

        // Initialize channel arrays
        const channels: Float32Array[] = [];
        for (let ch = 0; ch < numChannels; ch++) {
          channels.push(new Float32Array(numSamples));
        }

        // Read interleaved samples
        console.log('[parseWavFile] Starting sample read - dataOffset:', dataOffset, 'numSamples:', numSamples, 'bytesPerSample:', bytesPerSample);

        // Debug: check first few raw bytes
        if (numSamples > 0) {
          console.log('[parseWavFile] First 32 bytes at dataOffset:',
            Array.from(new Uint8Array(arrayBuffer, dataOffset, Math.min(32, dataSize))).map(b => b.toString(16).padStart(2, '0')).join(' '));
        }

        for (let i = 0; i < numSamples; i++) {
          for (let ch = 0; ch < numChannels; ch++) {
            const sampleOffset = dataOffset + (i * numChannels + ch) * bytesPerSample;
            let sample: number;

            if (audioFormat === 3) {
              // IEEE Float
              if (bitsPerSample === 32) {
                // 32-bit float
                sample = view.getFloat32(sampleOffset, true);
              } else if (bitsPerSample === 64) {
                // 64-bit float
                sample = view.getFloat64(sampleOffset, true);
              } else {
                console.error('[parseWavFile] Unsupported float bits per sample:', bitsPerSample);
                return null;
              }
            } else {
              // PCM integer - convert to float range [-1, 1]
              if (bitsPerSample === 8) {
                // 8-bit PCM is unsigned (0-255)
                const intSample = view.getUint8(sampleOffset);
                sample = (intSample - 128) / 128.0;
              } else if (bitsPerSample === 16) {
                // 16-bit PCM is signed
                const intSample = view.getInt16(sampleOffset, true);
                sample = intSample / 32768.0;
              } else if (bitsPerSample === 24) {
                // 24-bit PCM is signed (3 bytes)
                const byte1 = view.getUint8(sampleOffset);
                const byte2 = view.getUint8(sampleOffset + 1);
                const byte3 = view.getUint8(sampleOffset + 2);
                let intSample = (byte3 << 16) | (byte2 << 8) | byte1;
                // Sign extend
                if (intSample & 0x800000) {
                  intSample |= 0xFF000000;
                }
                sample = intSample / 8388608.0;
              } else if (bitsPerSample === 32) {
                // 32-bit PCM is signed
                const intSample = view.getInt32(sampleOffset, true);
                sample = intSample / 2147483648.0;
              } else {
                console.error('[parseWavFile] Unsupported PCM bits per sample:', bitsPerSample);
                return null;
              }
            }

            channels[ch][i] = sample;

            // Debug: log first few samples
            if (i < 5) {
              console.log(`[parseWavFile] Sample ${i} ch${ch}: ${sample}`);
            }
          }
        }

        const duration = numSamples / sampleRate;

        console.log('[parseWavFile] Successfully parsed WAV:', {
          audioFormat,
          numChannels,
          sampleRate,
          bitsPerSample,
          numSamples,
          duration
        });

        return { channels, sampleRate, duration };
      }

      offset += 8 + chunkSize;
    }

    console.log('[parseWavFile] No fmt or data chunk found (exited loop at offset:', offset, ')');
    return null;
  } catch (error) {
    console.error('[parseWavFile] EXCEPTION while parsing WAV:', error);
    console.error('[parseWavFile] Stack trace:', (error as Error).stack);
    return null;
  }
};

/**
 * Extract metadata from a video or audio blob using browser APIs (fallback).
 * Returns a promise that resolves to ApiVideoFileDetails.
 */
const extractMetadataWithBrowser = (
  blob: Blob,
  filename: string,
  fileType: ComparisonFileType,
): Promise<ApiVideoFileDetails> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);

    const metadata: ApiVideoFileDetails = {
      filename,
      size_bytes: blob.size,
      mime_type: blob.type || null,
    };

    if (fileType === "video") {
      const video = document.createElement("video");
      video.preload = "metadata";

      // Add timeout in case metadata never loads
      const timeout = setTimeout(() => {
        metadata.analysis_error = "Metadata loading timed out";
        URL.revokeObjectURL(url);
        resolve(metadata);
      }, 10000); // 10 second timeout

      video.onloadedmetadata = () => {
        clearTimeout(timeout);
        metadata.duration_seconds = video.duration;
        metadata.width = video.videoWidth;
        metadata.height = video.videoHeight;

        // Try to detect frame rate from the video
        // Browser doesn't expose exact frame rate, but we can estimate by trying common rates
        if (video.duration && video.duration > 0) {
          // Common frame rates to try
          const commonFrameRates = [23.976, 24, 25, 29.97, 30, 50, 59.94, 60];

          let bestFrameRate = 30; // Default fallback
          let bestError = Infinity;

          // Try each common frame rate and see which gives most integer-like frame count
          for (const fps of commonFrameRates) {
            const frameCount = video.duration * fps;
            const error = Math.abs(frameCount - Math.round(frameCount));

            // If this gives a more integer frame count, use it
            if (error < bestError) {
              bestError = error;
              bestFrameRate = fps;
            }
          }

          // Use detected frame rate if error is small enough (within 0.5 frames)
          // Otherwise fall back to 30fps
          if (bestError < 0.5) {
            metadata.frame_rate = bestFrameRate;
            metadata.frame_count = Math.round(video.duration * bestFrameRate);
          } else {
            metadata.frame_rate = 30;
            metadata.frame_count = Math.round(video.duration * 30);
          }
        }

        console.log(`[extractMediaMetadata] Video metadata extracted for ${filename}:`, {
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
          detectedFrameRate: metadata.frame_rate,
          estimatedFrames: metadata.frame_count,
        });

        URL.revokeObjectURL(url);
        resolve(metadata);
      };

      video.onerror = (e) => {
        clearTimeout(timeout);
        console.error(`[extractMediaMetadata] Failed to load video ${filename}:`, e);
        metadata.analysis_error = "Failed to load video metadata";
        URL.revokeObjectURL(url);
        resolve(metadata);
      };

      video.src = url;
      video.load(); // Explicitly trigger load
    } else if (fileType === "audio") {
      // Use Web Audio API to get detailed audio metadata
      blob.arrayBuffer().then((arrayBuffer) => {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

        // Make a copy of arrayBuffer before passing to decodeAudioData,
        // because decodeAudioData detaches/consumes it
        const arrayBufferCopy = arrayBuffer.slice(0);

        audioContext.decodeAudioData(
          arrayBufferCopy,
          (audioBuffer) => {
            metadata.duration_seconds = audioBuffer.duration;
            metadata.sample_rate = audioBuffer.sampleRate;
            metadata.channels = audioBuffer.numberOfChannels;

            if (metadata.sample_rate && metadata.duration_seconds) {
              metadata.sample_count = Math.round(
                metadata.sample_rate * metadata.duration_seconds
              );
            }

            // Estimate bit rate from file size and duration
            // This is approximate (includes container overhead) but useful when codec bit_rate is unavailable
            if (metadata.duration_seconds && metadata.duration_seconds > 0 && blob.size) {
              metadata.bit_rate = Math.round((blob.size * 8) / metadata.duration_seconds);
            }

            console.log(`[extractMediaMetadata] Audio metadata extracted for ${filename}:`, {
              duration: audioBuffer.duration,
              sampleRate: audioBuffer.sampleRate,
              channels: audioBuffer.numberOfChannels,
              estimatedBitRate: metadata.bit_rate,
            });

            URL.revokeObjectURL(url);
            audioContext.close();
            resolve(metadata);
          },
          (error) => {
            console.error(`[extractMediaMetadata] Failed to decode audio ${filename}:`, error);

            // Try manual WAV parsing to still extract metadata even if browser can't decode
            const parsedWav = parseWavFile(arrayBuffer);
            if (parsedWav) {
              console.log(`[extractMediaMetadata] Used manual WAV parser for metadata extraction: ${filename}`);
              metadata.duration_seconds = parsedWav.duration;
              metadata.sample_rate = parsedWav.sampleRate;
              metadata.channels = parsedWav.channels.length;
              metadata.sample_count = parsedWav.channels[0]?.length ?? 0;

              // Estimate bit rate from file size and duration
              if (metadata.duration_seconds && metadata.duration_seconds > 0 && blob.size) {
                metadata.bit_rate = Math.round((blob.size * 8) / metadata.duration_seconds);
              }

              metadata.analysis_error = "Audio format not supported for playback (waveform analysis only)";
            } else {
              metadata.analysis_error = "Failed to decode audio";
            }

            URL.revokeObjectURL(url);
            audioContext.close();
            resolve(metadata);
          }
        );
      }).catch((error) => {
        console.error(`[extractMediaMetadata] Failed to read audio file ${filename}:`, error);
        metadata.analysis_error = "Failed to read audio file";
        URL.revokeObjectURL(url);
        resolve(metadata);
      });
    } else {
      // Not a media file
      URL.revokeObjectURL(url);
      resolve(metadata);
    }
  });
};

/**
 * Extract metadata from a video or audio blob using FFprobe.
 * Falls back to browser APIs if FFprobe fails (with clear logging).
 */
const extractMediaMetadata = async (
  blob: Blob,
  filename: string,
  fileType: ComparisonFileType,
): Promise<ApiVideoFileDetails> => {
  // Wait for FFprobe to be ready (it's preloading in background)
  console.log(`[extractMediaMetadata] Waiting for FFprobe to be ready for ${filename}...`);
  const ffprobe = await getFFprobe(); // This will wait if still loading

  // Try FFprobe first (more accurate and detailed)
  const result = await extractMetadataWithFFprobe(blob, filename, ffprobe);

  if (result) {
    console.log(`[extractMediaMetadata] Successfully extracted metadata with FFprobe for ${filename}`);
    return result;
  }

  // FFprobe failed - fall back to browser APIs (less detailed but more compatible)
  console.warn(`[extractMediaMetadata] FFprobe failed for ${filename}, falling back to browser APIs`);
  const browserResult = await extractMetadataWithBrowser(blob, filename, fileType);
  console.log(`[extractMediaMetadata] Extracted metadata with browser APIs for ${filename}:`, browserResult);
  return browserResult;
};
const VideoComparisonModal: React.FC<VideoComparisonModalProps> = ({
  isOpen,
  onClose,
  problemRunId,
  isTutorial = false,
}) => {
  const [videoPairs, setVideoPairs] = useState<VideoPair[]>([]);
  const [selectedPairIndex, setSelectedPairIndex] = useState(0);
  const [isSideBySideMode, setIsSideBySideMode] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState({
    aiOutput: { loaded: 0, total: 0 },
    solution: { loaded: 0, total: 0 },
  });

  const [video1File, setVideo1File] = useState<File | null>(null);
  const [video2File, setVideo2File] = useState<File | null>(null);
  const [video1Url, setVideo1Url] = useState<string | null>(null);
  const [video2Url, setVideo2Url] = useState<string | null>(null);
  const [diffPdfUrl, setDiffPdfUrl] = useState<string | null>(null);
  const [video1Duration, setVideo1Duration] = useState<number | null>(null);
  const [video2Duration, setVideo2Duration] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [lengthMismatch, setLengthMismatch] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioPlayable, setIsAudioPlayable] = useState(true); // Track if audio can be played by browser
  const [isDraggingVideo1, setIsDraggingVideo1] = useState(false);
  const [isDraggingVideo2, setIsDraggingVideo2] = useState(false);
  const [unmatchedSolutionFiles, setUnmatchedSolutionFiles] = useState<
    string[]
  >([]);
  const [unsupportedCodecError, setUnsupportedCodecError] = useState<
    string | null
  >(null);
  const [solutionTranscodeMetadata, setSolutionTranscodeMetadata] =
    useState<any>(null);
  const [aiTranscodeMetadata, setAiTranscodeMetadata] = useState<any>(null);
  const [showSolutionMetadata, setShowSolutionMetadata] = useState(false);
  const [showAiMetadata, setShowAiMetadata] = useState(false);
  const [currentFileType, setCurrentFileType] =
    useState<ComparisonFileType>("video");
  const [solutionTextContent, setSolutionTextContent] = useState<string | null>(
    null,
  );
  const [aiTextContent, setAiTextContent] = useState<string | null>(null);
  const [imageDiffUrl, setImageDiffUrl] = useState<string | null>(null);
  const [imageDiffLoading, setImageDiffLoading] = useState(false);
  const [imageDimensionMismatch, setImageDimensionMismatch] = useState<{
    solution: { width: number; height: number };
    ai: { width: number; height: number };
  } | null>(null);
  const [differenceStats, setDifferenceStats] = useState<{ peak: number; rms: number } | null>(
    null,
  );
  const [currentDbLevels, setCurrentDbLevels] = useState<{
    solution: number[];
    aiOutput: number[];
  } | null>(null);

  const video1Ref = useRef<HTMLVideoElement | null>(null);
  const video2Ref = useRef<HTMLVideoElement | null>(null);
  const sliderRef = useRef<HTMLInputElement>(null);
  const isSliderDraggingRef = useRef<boolean>(false);
  const dragCounterVideo1Ref = useRef<number>(0);
  const dragCounterVideo2Ref = useRef<number>(0);
  const video1UrlRef = useRef<string | null>(null);
  const video2UrlRef = useRef<string | null>(null);
  const diffPdfUrlRef = useRef<string | null>(null);
  const waveformCanvas1Ref = useRef<HTMLCanvasElement>(null);
  const waveformCanvas2Ref = useRef<HTMLCanvasElement>(null);
  const waveformImage1Ref = useRef<ImageData | null>(null);
  const waveformImage2Ref = useRef<ImageData | null>(null);
  const waveformCanvasDiffRef = useRef<HTMLCanvasElement>(null);
  const waveformImageDiffRef = useRef<ImageData | null>(null);
  const solutionAudioDataRef = useRef<Float32Array[] | null>(null); // Now array of channels
  const aiAudioDataRef = useRef<Float32Array[] | null>(null); // Now array of channels
  const solutionAudioSampleRateRef = useRef<number | null>(null);
  const aiAudioSampleRateRef = useRef<number | null>(null);
  const solutionAudioChannelCountRef = useRef<number | null>(null);
  const aiAudioChannelCountRef = useRef<number | null>(null);
  // Web Audio API refs for playback
  const audioContextRef = useRef<AudioContext | null>(null);
  const solutionAudioBufferRef = useRef<AudioBuffer | null>(null);
  const aiAudioBufferRef = useRef<AudioBuffer | null>(null);
  const solutionSourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const aiSourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const audioStartTimeRef = useRef<number>(0);
  const audioCurrentOffsetRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const solutionImageRef = useRef<HTMLImageElement | null>(null);
  const aiImageRef = useRef<HTMLImageElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const playersWrapperRef = useRef<HTMLDivElement | null>(null);
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const sideBySideHeaderRef = useRef<HTMLDivElement | null>(null);
  const sideBySideMetadataRef = useRef<HTMLDivElement | null>(null);
  const [solutionImageDimensions, setSolutionImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [aiImageDimensions, setAiImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [solutionHoverCoords, setSolutionHoverCoords] =
    useState<HoverCoords | null>(null);
  const [aiHoverCoords, setAiHoverCoords] = useState<HoverCoords | null>(null);
  const [diffHoverCoords, setDiffHoverCoords] = useState<HoverCoords | null>(
    null,
  );

  // PDF diff states
  const [pdfDiffUrls, setPdfDiffUrls] = useState<string[]>([]);
  const [pdfDiffLoading, setPdfDiffLoading] = useState(false);
  const [pdfDiffDetected, setPdfDiffDetected] = useState(false);
  const [pdfDiffPages, setPdfDiffPages] = useState<number[]>([]);
  const [pdfDimensionMismatch, setPdfDimensionMismatch] = useState<{
    solution: { numPages: number; width: number; height: number };
    ai: { numPages: number; width: number; height: number };
  } | null>(null);
  const [modalSize, setModalSize] = useState({ width: 0, height: 0 });
  const [playerAreaHeight, setPlayerAreaHeight] = useState<number | null>(null);
  const [sideBySideVideoDimensions, setSideBySideVideoDimensions] = useState<
    { width: number; height: number } | null
  >(null);

  const clearWaveform = (
    canvasRef: React.RefObject<HTMLCanvasElement | null>,
    imageDataRef: React.MutableRefObject<ImageData | null>,
  ) => {
    const canvas = canvasRef.current;
    imageDataRef.current = null;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  /**
   * Parse WAV file manually to support formats like 64-bit float PCM that browsers don't natively support.
   * Returns null if not a WAV file or if parsing fails.
   */
  const decodeAudioSource = async (source: File | Blob) => {
    const AudioContextCtor = (window.AudioContext ||
      (window as any).webkitAudioContext) as typeof AudioContext | undefined;

    if (!AudioContextCtor) {
      throw new Error("Web Audio API not supported in this browser.");
    }

    const arrayBuffer = await source.arrayBuffer();
    console.log('[decodeAudioSource] Got arrayBuffer, size:', arrayBuffer.byteLength, 'source:', source);

    // First, try to extract the sample rate from the WAV header to avoid resampling
    let detectedSampleRate: number | undefined;
    try {
      const view = new DataView(arrayBuffer);
      const riff = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
      const wave = String.fromCharCode(view.getUint8(8), view.getUint8(9), view.getUint8(10), view.getUint8(11));

      if (riff === 'RIFF' && wave === 'WAVE') {
        // Look for fmt chunk
        let offset = 12;
        while (offset < arrayBuffer.byteLength - 8) {
          const chunkId = String.fromCharCode(view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3));
          const chunkSize = view.getUint32(offset + 4, true);

          if (chunkId === 'fmt ') {
            detectedSampleRate = view.getUint32(offset + 12, true);
            console.log('[decodeAudioSource] Detected sample rate from WAV header:', detectedSampleRate);
            break;
          }

          offset += 8 + chunkSize;
        }
      }
    } catch (e) {
      console.log('[decodeAudioSource] Could not detect sample rate from header:', e);
    }

    // Create AudioContext with the detected sample rate to avoid resampling
    const context = detectedSampleRate
      ? new AudioContextCtor({ sampleRate: detectedSampleRate })
      : new AudioContextCtor();

    console.log('[decodeAudioSource] Created AudioContext with sample rate:', context.sampleRate);

    try {
      // Try native browser decoding first (for best compatibility and playback support)
      try {
        console.log('[decodeAudioSource] Trying native browser decoding...');
        const audioBuffer = await context.decodeAudioData(arrayBuffer.slice(0));
        console.log('[decodeAudioSource] Native decode succeeded - sampleRate:', audioBuffer.sampleRate, 'length:', audioBuffer.length, 'duration:', audioBuffer.duration, 'channels:', audioBuffer.numberOfChannels);

        // Extract all channels
        const channels: Float32Array[] = [];
        const numChannels = audioBuffer.numberOfChannels;

        for (let ch = 0; ch < numChannels; ch++) {
          const channelData = audioBuffer.getChannelData(ch);
          const dataCopy = new Float32Array(channelData.length);
          dataCopy.set(channelData);
          channels.push(dataCopy);

          // Debug first few samples
          if (ch === 0) {
            console.log('[decodeAudioSource] First 10 samples ch0:', Array.from(channelData.slice(0, 10)).map(s => s.toFixed(6)).join(', '));
          }
        }

        return {
          channels,
          channelCount: numChannels,
          sampleRate: audioBuffer.sampleRate,
          duration: audioBuffer.duration,
          sampleCount: channels[0]?.length ?? 0,
          audioBuffer, // Return the buffer for Web Audio API playback
        };
      } catch (nativeDecodeError) {
        // Native decoding failed, try manual WAV parsing for unsupported formats
        console.log('[decodeAudioSource] Native decode failed, trying manual WAV parser...', nativeDecodeError);
        const parsedWav = parseWavFile(arrayBuffer);
        if (parsedWav) {
          console.log('[decodeAudioSource] Used manual WAV parser for', source, 'size:', arrayBuffer.byteLength);
          // Create a dummy AudioBuffer-like object for compatibility
          const dummyAudioBuffer = {
            duration: parsedWav.duration,
            sampleRate: parsedWav.sampleRate,
            numberOfChannels: parsedWav.channels.length,
            length: parsedWav.channels[0]?.length ?? 0,
            getChannelData: (channel: number) => {
              if (channel < 0 || channel >= parsedWav.channels.length) {
                throw new RangeError('Channel index out of range');
              }
              return parsedWav.channels[channel];
            },
          };

          return {
            channels: parsedWav.channels,
            channelCount: parsedWav.channels.length,
            sampleRate: parsedWav.sampleRate,
            duration: parsedWav.duration,
            sampleCount: parsedWav.channels[0]?.length ?? 0,
            audioBuffer: dummyAudioBuffer as AudioBuffer, // Won't work for playback but fine for analysis
          };
        }

        // Both methods failed
        throw new Error('Failed to decode audio with both native and manual methods');
      }
    } finally {
      void context.close().catch(() => undefined);
    }
  };

  const computeWaveformPeaks = (data: Float32Array, width: number) => {
    const minPeaks = new Float32Array(width);
    const maxPeaks = new Float32Array(width);

    if (width === 0 || data.length === 0) {
      return { min: minPeaks, max: maxPeaks };
    }

    for (let i = 0; i < width; i++) {
      const start = Math.floor((i / width) * data.length);
      const end =
        i === width - 1
          ? data.length
          : Math.floor(((i + 1) / width) * data.length);

      let min = Infinity;
      let max = -Infinity;

      for (let j = start; j < end; j++) {
        const sample = data[j];
        if (sample < min) min = sample;
        if (sample > max) max = sample;
      }

      if (end <= start && data.length > 0) {
        const sample = data[Math.min(start, data.length - 1)];
        if (sample < min) min = sample;
        if (sample > max) max = sample;
      }

      minPeaks[i] = Number.isFinite(min) ? min : 0;
      maxPeaks[i] = Number.isFinite(max) ? max : 0;
    }

    return { min: minPeaks, max: maxPeaks };
  };

  const drawWaveformFromData = (
    channels: Float32Array[],
    canvasRef: React.RefObject<HTMLCanvasElement | null>,
    imageDataRef: React.MutableRefObject<ImageData | null>,
    color: string,
  ) => {
    const canvas = canvasRef.current;
    if (!canvas || channels.length === 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { width, height } = canvas;
    ctx.clearRect(0, 0, width, height);

    const numChannels = channels.length;
    const channelHeight = height / numChannels;

    // Draw each channel stacked vertically
    for (let ch = 0; ch < numChannels; ch++) {
      const data = channels[ch];
      if (!data) continue;

      const peaks = computeWaveformPeaks(data, width);
      const yOffset = ch * channelHeight;
      const amp = channelHeight / 2;
      const centerY = yOffset + amp;

      // Draw a subtle divider line between channels (except for the first one)
      if (ch > 0) {
        ctx.strokeStyle = "rgba(200, 200, 200, 0.3)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, yOffset);
        ctx.lineTo(width, yOffset);
        ctx.stroke();
      }

      // Draw channel label
      ctx.fillStyle = "rgba(100, 100, 100, 0.6)";
      ctx.font = "10px sans-serif";
      const channelLabel =
        numChannels === 2 ? (ch === 0 ? "L" : "R") : `Ch${ch + 1}`;
      ctx.fillText(channelLabel, 4, yOffset + 12);

      // Draw waveform
      ctx.fillStyle = color;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;

      for (let i = 0; i < width; i++) {
        const min = Math.max(-1, Math.min(1, peaks.min[i] ?? 0));
        const max = Math.max(-1, Math.min(1, peaks.max[i] ?? 0));
        const y1 = centerY + min * amp;
        const y2 = centerY + max * amp;
        const rectHeight = Math.max(1, y2 - y1);
        ctx.fillRect(i, y1, 1, rectHeight);
      }

      // Draw center line for this channel
      ctx.strokeStyle = "rgba(150, 150, 150, 0.2)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, centerY);
      ctx.lineTo(width, centerY);
      ctx.stroke();
    }

    imageDataRef.current = ctx.getImageData(0, 0, width, height);
  };

  const computeDifferencePeaksAndStats = (
    solutionData: Float32Array,
    aiData: Float32Array,
    width: number,
  ) => {
    const minPeaks = new Float32Array(width);
    const maxPeaks = new Float32Array(width);

    let peak = 0;
    let sumSquares = 0;
    let samplesCounted = 0;

    const solLen = solutionData.length;
    const aiLen = aiData.length;
    const maxLen = Math.max(solLen, aiLen);

    console.log('[computeDifferencePeaksAndStats] solLen:', solLen, 'aiLen:', aiLen, 'width:', width);

    if (width === 0 || maxLen === 0) {
      return {
        peaks: { min: minPeaks, max: maxPeaks },
        stats: { peak: 0, rms: 0 },
      };
    }

    // Debug: log first few samples from both inputs and their differences
    const firstSamples = Math.min(10, solLen, aiLen);
    for (let i = 0; i < firstSamples; i++) {
      const solSample = solutionData[i];
      const aiSample = aiData[i];
      const diffSample = solSample - aiSample;
      console.log(`[computeDifferencePeaksAndStats] Sample ${i}: sol=${solSample.toFixed(6)}, ai=${aiSample.toFixed(6)}, diff=${diffSample.toFixed(6)}`);
    }

    for (let i = 0; i < width; i++) {
      const start = Math.floor((i / width) * maxLen);
      const end =
        i === width - 1 ? maxLen : Math.floor(((i + 1) / width) * maxLen);

      let min = Infinity;
      let max = -Infinity;
      const range = Math.max(end - start, 1);

      for (let j = 0; j < range; j++) {
        const idx = start + j;
        if (idx >= maxLen) break;

        // Direct sample comparison - no time-based resampling
        const solSample = idx < solLen ? solutionData[idx] : 0;
        const aiSample = idx < aiLen ? aiData[idx] : 0;

        const diffSample = solSample - aiSample;
        if (diffSample < min) min = diffSample;
        if (diffSample > max) max = diffSample;

        const absVal = Math.abs(diffSample);
        if (absVal > peak) peak = absVal;
        sumSquares += diffSample * diffSample;
        samplesCounted++;
      }

      minPeaks[i] = Number.isFinite(min) ? min : 0;
      maxPeaks[i] = Number.isFinite(max) ? max : 0;
    }

    const rms = samplesCounted > 0 ? Math.sqrt(sumSquares / samplesCounted) : 0;

    console.log('[computeDifferencePeaksAndStats] peak:', peak, 'rms:', rms);

    return {
      peaks: { min: minPeaks, max: maxPeaks },
      stats: { peak, rms },
    };
  };

  const renderDifferenceWaveform = useCallback(() => {
    const canvas = waveformCanvasDiffRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const solutionChannels = solutionAudioDataRef.current;
    const aiChannels = aiAudioDataRef.current;

    console.log('[renderDifferenceWaveform] solutionChannels:', solutionChannels?.length, 'aiChannels:', aiChannels?.length);
    if (solutionChannels) {
      for (let i = 0; i < solutionChannels.length; i++) {
        console.log(`[renderDifferenceWaveform] solutionChannels[${i}].length:`, solutionChannels[i]?.length);
      }
    }
    if (aiChannels) {
      for (let i = 0; i < aiChannels.length; i++) {
        console.log(`[renderDifferenceWaveform] aiChannels[${i}].length:`, aiChannels[i]?.length);
      }
    }

    if (!solutionChannels || !aiChannels) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      waveformImageDiffRef.current = null;
      setDifferenceStats(null);
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Compare channels pairwise (pad with zeros if channel counts differ)
    const maxChannels = Math.max(solutionChannels.length, aiChannels.length);
    const channelHeight = canvas.height / maxChannels;

    let overallPeak = 0;
    let overallSumSquares = 0;
    let overallSamplesCounted = 0;

    for (let ch = 0; ch < maxChannels; ch++) {
      const solutionData = solutionChannels[ch] ?? new Float32Array(0);
      const aiData = aiChannels[ch] ?? new Float32Array(0);

      const { peaks, stats } = computeDifferencePeaksAndStats(
        solutionData,
        aiData,
        canvas.width,
      );

      if (stats.peak > overallPeak) overallPeak = stats.peak;
      overallSumSquares += stats.rms * stats.rms;
      overallSamplesCounted++;

      const yOffset = ch * channelHeight;
      const amp = channelHeight / 2;
      const centerY = yOffset + amp;

      // Draw divider line between channels
      if (ch > 0) {
        ctx.strokeStyle = "rgba(200, 200, 200, 0.3)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, yOffset);
        ctx.lineTo(canvas.width, yOffset);
        ctx.stroke();
      }

      // Draw channel label
      ctx.fillStyle = "rgba(100, 100, 100, 0.6)";
      ctx.font = "10px sans-serif";
      const channelLabel =
        maxChannels === 2 ? (ch === 0 ? "L" : "R") : `Ch${ch + 1}`;
      ctx.fillText(channelLabel, 4, yOffset + 12);

      // Draw difference waveform
      ctx.fillStyle = "#f97316";
      ctx.strokeStyle = "#f97316";
      ctx.lineWidth = 1;

      for (let i = 0; i < canvas.width; i++) {
        const min = Math.max(-1, Math.min(1, peaks.min[i] ?? 0));
        const max = Math.max(-1, Math.min(1, peaks.max[i] ?? 0));
        const y1 = centerY + min * amp;
        const y2 = centerY + max * amp;
        const rectHeight = Math.max(1, y2 - y1);
        ctx.fillRect(i, y1, 1, rectHeight);
      }

      // Draw center line
      ctx.strokeStyle = "rgba(150, 150, 150, 0.2)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, centerY);
      ctx.lineTo(canvas.width, centerY);
      ctx.stroke();
    }

    waveformImageDiffRef.current = ctx.getImageData(
      0,
      0,
      canvas.width,
      canvas.height,
    );

    // Compute overall RMS from channel RMS values
    const overallRms =
      overallSamplesCounted > 0
        ? Math.sqrt(overallSumSquares / overallSamplesCounted)
        : 0;

    setDifferenceStats({ peak: overallPeak, rms: overallRms });
  }, []);

  const calculateCurrentDbLevels = useCallback((time: number) => {
    const solutionChannels = solutionAudioDataRef.current;
    const aiChannels = aiAudioDataRef.current;
    const solutionSampleRate = solutionAudioSampleRateRef.current;
    const aiSampleRate = aiAudioSampleRateRef.current;

    if (!solutionChannels || !aiChannels || !solutionSampleRate || !aiSampleRate) {
      setCurrentDbLevels(null);
      return;
    }

    // Calculate sample index for current time
    const solutionSampleIndex = Math.floor(time * solutionSampleRate);
    const aiSampleIndex = Math.floor(time * aiSampleRate);

    const solutionDbs: number[] = [];
    const aiDbs: number[] = [];

    // Calculate for solution channels - using instantaneous sample value
    for (let ch = 0; ch < solutionChannels.length; ch++) {
      const channel = solutionChannels[ch];
      if (!channel) continue;

      if (solutionSampleIndex >= 0 && solutionSampleIndex < channel.length) {
        const sampleValue = Math.abs(channel[solutionSampleIndex]);
        const db = sampleValue > 0 ? 20 * Math.log10(sampleValue) : -Infinity;
        solutionDbs.push(db);
      } else {
        solutionDbs.push(-Infinity);
      }
    }

    // Calculate for AI channels - using instantaneous sample value
    for (let ch = 0; ch < aiChannels.length; ch++) {
      const channel = aiChannels[ch];
      if (!channel) continue;

      if (aiSampleIndex >= 0 && aiSampleIndex < channel.length) {
        const sampleValue = Math.abs(channel[aiSampleIndex]);
        const db = sampleValue > 0 ? 20 * Math.log10(sampleValue) : -Infinity;
        aiDbs.push(db);
      } else {
        aiDbs.push(-Infinity);
      }
    }

    setCurrentDbLevels({
      solution: solutionDbs,
      aiOutput: aiDbs,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      // Cleanup URLs when modal closes
      if (video1UrlRef.current) {
        URL.revokeObjectURL(video1UrlRef.current);
        video1UrlRef.current = null;
      }
      if (video2UrlRef.current) {
        URL.revokeObjectURL(video2UrlRef.current);
        video2UrlRef.current = null;
      }
      if (diffPdfUrlRef.current) {
        URL.revokeObjectURL(diffPdfUrlRef.current);
        diffPdfUrlRef.current = null;
      }
      setVideo1File(null);
      setVideo2File(null);
      setVideo1Url(null);
      setVideo2Url(null);
      setDiffPdfUrl(null);
      setVideo1Duration(null);
      setVideo2Duration(null);
      setCurrentTime(0);
      setLengthMismatch(false);
      setIsPlaying(false);
      setVideoPairs([]);
      setUnmatchedSolutionFiles([]);
      setSelectedPairIndex(0);
      setLoadingVideos(false);
      setLoadingError(null);
      setUnsupportedCodecError(null);
      setDownloadProgress({
        aiOutput: { loaded: 0, total: 0 },
        solution: { loaded: 0, total: 0 },
      });
      // Clear metadata state
      setSolutionTranscodeMetadata(null);
      setAiTranscodeMetadata(null);
      setShowSolutionMetadata(false);
      setShowAiMetadata(false);
      // Clear waveform canvases
      clearWaveform(waveformCanvas1Ref, waveformImage1Ref);
      clearWaveform(waveformCanvas2Ref, waveformImage2Ref);
      clearWaveform(waveformCanvasDiffRef, waveformImageDiffRef);
      solutionAudioDataRef.current = null;
      aiAudioDataRef.current = null;
      solutionAudioSampleRateRef.current = null;
      aiAudioSampleRateRef.current = null;
      solutionAudioBufferRef.current = null;
      aiAudioBufferRef.current = null;
      setDifferenceStats(null);
      setCurrentFileType("video");
      // Stop audio playback and close audio context
      if (solutionSourceNodeRef.current) {
        try {
          solutionSourceNodeRef.current.stop();
        } catch (e) {
          // Ignore
        }
        solutionSourceNodeRef.current = null;
      }
      if (aiSourceNodeRef.current) {
        try {
          aiSourceNodeRef.current.stop();
        } catch (e) {
          // Ignore
        }
        aiSourceNodeRef.current = null;
      }
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (audioContextRef.current) {
        void audioContextRef.current.close().catch(() => undefined);
        audioContextRef.current = null;
      }
      audioStartTimeRef.current = 0;
      audioCurrentOffsetRef.current = 0;
      setSolutionTextContent(null);
      setAiTextContent(null);
      setImageDiffUrl(null);
      setImageDiffLoading(false);
      setImageDimensionMismatch(null);
      setSolutionImageDimensions(null);
      setAiImageDimensions(null);
      setSolutionHoverCoords(null);
      setAiHoverCoords(null);
      setDiffHoverCoords(null);
      setSideBySideVideoDimensions(null);
    }
  }, [isOpen, video1Url, video2Url]);

  useEffect(() => {
    if (!isSideBySideMode) {
      setSideBySideVideoDimensions(null);
    }
  }, [isSideBySideMode]);

  useEffect(() => {
    if (!isOpen) {
      setModalSize({ width: 0, height: 0 });
      return;
    }

    const updateModalSize = () => {
      if (!modalRef.current) return;
      const rect = modalRef.current.getBoundingClientRect();
      setModalSize({ width: rect.width, height: rect.height });
    };

    updateModalSize();
    window.addEventListener("resize", updateModalSize);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateModalSize();
      });
      if (modalRef.current) {
        resizeObserver.observe(modalRef.current);
      }
    }

    return () => {
      window.removeEventListener("resize", updateModalSize);
      resizeObserver?.disconnect();
    };
  }, [isOpen]);

  const loadVideoPair = useCallback(async (pair: VideoPair) => {
    if (video1UrlRef.current) {
      URL.revokeObjectURL(video1UrlRef.current);
      video1UrlRef.current = null;
    }
    if (video2UrlRef.current) {
      URL.revokeObjectURL(video2UrlRef.current);
      video2UrlRef.current = null;
    }
    if (diffPdfUrlRef.current) {
      URL.revokeObjectURL(diffPdfUrlRef.current);
      diffPdfUrlRef.current = null;
    }

    setVideo1Url(null);
    setVideo2Url(null);
    setDiffPdfUrl(null);
    setVideo1Duration(null);
    setVideo2Duration(null);
    setSolutionTextContent(null);
    setAiTextContent(null);
    solutionAudioDataRef.current = null;
    aiAudioDataRef.current = null;
    solutionAudioSampleRateRef.current = null;
    aiAudioSampleRateRef.current = null;
    solutionAudioBufferRef.current = null;
    aiAudioBufferRef.current = null;
    setDifferenceStats(null);
    // Stop audio playback when switching pairs
    if (solutionSourceNodeRef.current) {
      try {
        solutionSourceNodeRef.current.stop();
      } catch (e) {
        // Ignore
      }
      solutionSourceNodeRef.current = null;
    }
    if (aiSourceNodeRef.current) {
      try {
        aiSourceNodeRef.current.stop();
      } catch (e) {
        // Ignore
      }
      aiSourceNodeRef.current = null;
    }
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    audioCurrentOffsetRef.current = 0;
    setImageDiffUrl(null);
    setImageDiffLoading(false);
    setImageDimensionMismatch(null);
    setSolutionImageDimensions(null);
    setAiImageDimensions(null);
    setSolutionHoverCoords(null);
    setAiHoverCoords(null);
    setDiffHoverCoords(null);
    clearWaveform(waveformCanvas1Ref, waveformImage1Ref);
    clearWaveform(waveformCanvas2Ref, waveformImage2Ref);
    clearWaveform(waveformCanvasDiffRef, waveformImageDiffRef);
    setCurrentFileType(pair.fileType);
    setCurrentTime(0);
    setIsPlaying(false);
    setUnsupportedCodecError(null);

    const describePair = () => ({
      filename: pair.filename,
      solutionFilename: pair.solutionFilename,
      solutionType: pair.solutionBlob.type,
      aiType: pair.aiOutputBlob.type,
      solutionSize: pair.solutionBlob.size,
      aiSize: pair.aiOutputBlob.size,
      fileType: pair.fileType,
    });

    // Handle PDF files (pre-generated from LibreOffice/Office documents)
    if (pair.fileType === "pdf") {
      console.log(
        "[VideoComparisonModal] Loading pre-generated PDF pair",
        describePair(),
      );

      // PDFs are already converted, just create object URLs
      const solUrl = URL.createObjectURL(pair.solutionBlob);
      const aiUrl = URL.createObjectURL(pair.aiOutputBlob);

      setVideo1Url(solUrl);
      setVideo2Url(aiUrl);
      video1UrlRef.current = solUrl;
      video2UrlRef.current = aiUrl;

      // If we have a diff PDF, set it too
      if (pair.diffBlob) {
        const diffUrl = URL.createObjectURL(pair.diffBlob);
        setDiffPdfUrl(diffUrl);
        diffPdfUrlRef.current = diffUrl;
      }

      setCurrentFileType("pdf");
      setLoadingVideos(false);
      return;
    }

    if (pair.fileType === "text") {
      console.log("[VideoComparisonModal] Loading text pair", describePair());
      const [solutionText, aiText] = await Promise.all([
        pair.solutionBlob.text().catch(() => "Unable to read text content."),
        pair.aiOutputBlob.text().catch(() => "Unable to read text content."),
      ]);
      setSolutionTextContent(solutionText);
      setAiTextContent(aiText);
      video1UrlRef.current = null;
      video2UrlRef.current = null;
      return;
    }

    // Handle PSD files by converting them to PNG blobs first
    let solutionBlobToUse = pair.solutionBlob;
    let aiOutputBlobToUse = pair.aiOutputBlob;

    if (pair.fileType === "image" && pair.filename.toLowerCase().endsWith(".psd")) {
      console.log("[VideoComparisonModal] Loading PSD pair", describePair());
      try {
        [solutionBlobToUse, aiOutputBlobToUse] = await Promise.all([
          renderPsdToBlob(pair.solutionBlob),
          renderPsdToBlob(pair.aiOutputBlob),
        ]);
        console.log("[VideoComparisonModal] PSD conversion successful");
      } catch (error) {
        console.error("[VideoComparisonModal] Failed to convert PSD:", error);
        setLoadingError(`Failed to render PSD file: ${error instanceof Error ? error.message : "Unknown error"}`);
        setLoadingVideos(false);
        return;
      }
    }

    const solUrl = URL.createObjectURL(solutionBlobToUse);
    const aiUrl = URL.createObjectURL(aiOutputBlobToUse);

    console.log("[VideoComparisonModal] Loading media pair", {
      ...describePair(),
      solUrl,
      aiUrl,
    });

    setVideo1Url(solUrl);
    setVideo2Url(aiUrl);
    video1UrlRef.current = solUrl;
    video2UrlRef.current = aiUrl;
  }, []);

  const fetchAndProcessVideos = useCallback(async () => {
    if (!problemRunId) return;

    console.log('[VideoComparisonModal] Using problemRunId:', problemRunId);

    setLoadingVideos(true);
    setLoadingError(null);

    try {
      // First, fetch the problem run to get the software type
      const problemRunResponse = await apiClient.get(
        `/problem-runs/${problemRunId}`,
      );
      const problemRun = problemRunResponse.data;
      console.log('[VideoComparisonModal] Problem run data:', problemRun);

      // Get the problem to determine software type
      const problemResponse = await apiClient.get(
        `/problems/${problemRun.problem_id}`,
      );
      const problem = problemResponse.data;
      const software = problem.software || problem.spec?.software_type;
      console.log('[VideoComparisonModal] Software type:', software);

      // For Shotcut and LibreOffice, try to use the solution comparisons endpoint to fetch manifest.json
      // If it succeeds, use side-by-side videos. If it fails, fall back to old behavior.
      if (software === "shotcut" || software === "libreoffice") {
        try {
          // First, get the solution_id for this problem
          const solutionResponse = await apiClient.get(
            `/solutions/${problemRun.problem_id}`,
          );
          const solutionData = solutionResponse.data;

          if (!solutionData.exists) {
            console.log('[VideoComparisonModal] No solution found for this problem, falling back to old behavior');
            // Fall through to the old behavior below
          } else {
            const solutionId = solutionData.solution_id;
            console.log('[VideoComparisonModal] Found solution_id:', solutionId);

            const manifestUrl = `/solutions/${solutionId}/comparisons/${problemRunId}/manifest.json`;
            console.log(
              '[VideoComparisonModal] Fetching manifest from:',
              manifestUrl,
            );

            // Poll for manifest completion if it's in progress
            const manifest = await (async () => {
              const maxAttempts = 60; // Poll for up to 2 minutes (60 * 2s)
              let attempts = 0;

              while (attempts < maxAttempts) {
                try {
                  const manifestResponse = await apiClient.get(manifestUrl);
                  const manifestData = manifestResponse.data;

                  console.log('[VideoComparisonModal] Manifest data:', manifestData);

                  if (manifestData.status === 'in_progress') {
                    console.log('[VideoComparisonModal] Manifest is in_progress, polling again in 2s...');
                    attempts++;
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    continue;
                  }

                  // Status is completed or failed, return the manifest
                  return manifestData;
                } catch (error) {
                  console.log('[VideoComparisonModal] Error fetching manifest:', error);
                  throw error;
                }
              }

              throw new Error('Manifest generation timed out after 2 minutes');
            })();

            console.log('[VideoComparisonModal] Final manifest:', manifest);

            // Find files with membership "both" that have side-by-side previews
            const matchedFiles = manifest.files?.filter(
              (file: any) =>
                file.membership === "both" && file.side_by_side_preview,
            ) || [];

            console.log('[VideoComparisonModal] Matched files from manifest:', matchedFiles.length, matchedFiles);

            if (matchedFiles.length > 0) {
              console.log(
                '[VideoComparisonModal] Using side-by-side mode with pre-generated videos',
              );
              setIsSideBySideMode(true);

              // Build video pairs from matched files
              const pairs: VideoPair[] = await Promise.all(
                matchedFiles.map(async (file: any) => {
                  const filename = file.path;
                  const fileTypeInfo = getFileTypeInfo(filename);

                  // Check if we have multiple paths (e.g., for PDFs: left, right, diff)
                  const hasPaths = file.side_by_side_preview.paths && Array.isArray(file.side_by_side_preview.paths);
                  const paths = hasPaths ? file.side_by_side_preview.paths : [file.side_by_side_preview.path];

                  // For LibreOffice files converted to PDF with 3 files (left, right, diff), fetch only left and right (use client-side diff)
                  // For native PDFs with 2 files (left, right), also fetch both (use client-side diff)
                  if ((fileTypeInfo.type === "docx" && paths.length === 3) ||
                      (fileTypeInfo.type === "pdf" && paths.length === 2)) {
                    console.log('[VideoComparisonModal] Fetching two PDFs (left, right) - will use client-side diff:', paths);

                    const [leftResponse, rightResponse] = await Promise.all([
                      apiClient.get(`/solutions/${solutionId}/comparisons/${problemRunId}/${paths[0]}`, { responseType: 'blob' }),
                      apiClient.get(`/solutions/${solutionId}/comparisons/${problemRunId}/${paths[1]}`, { responseType: 'blob' }),
                    ]);

                    const solutionBlob = new Blob([leftResponse.data], { type: "application/pdf" });
                    const aiOutputBlob = new Blob([rightResponse.data], { type: "application/pdf" });

                    // Create metadata object from manifest data
                    const metadata: ApiVideoComparisonPair = {
                      filename: filename,
                      solution: {
                        filename: filename,
                        url: `/solutions/${solutionId}/comparisons/${problemRunId}/${paths[0]}`,
                        size_bytes: file.left_metadata?.size_bytes || solutionBlob.size,
                        mime_type: file.left_metadata?.mime_type || 'application/pdf',
                        duration_seconds: file.left_metadata?.duration_seconds || null,
                        frame_count: file.left_metadata?.frame_count || null,
                        frame_rate: file.left_metadata?.frame_rate || null,
                        width: file.left_metadata?.width || null,
                        height: file.left_metadata?.height || null,
                        codec: file.left_metadata?.codec || null,
                      },
                      ai_output: {
                        filename: filename,
                        url: `/solutions/${solutionId}/comparisons/${problemRunId}/${paths[1]}`,
                        size_bytes: file.right_metadata?.size_bytes || aiOutputBlob.size,
                        mime_type: file.right_metadata?.mime_type || 'application/pdf',
                        duration_seconds: file.right_metadata?.duration_seconds || null,
                        frame_count: file.right_metadata?.frame_count || null,
                        frame_rate: file.right_metadata?.frame_rate || null,
                        width: file.right_metadata?.width || null,
                        height: file.right_metadata?.height || null,
                        codec: file.right_metadata?.codec || null,
                      },
                    };

                    return {
                      filename,
                      solutionFilename: filename,
                      fileType: "pdf" as ComparisonFileType, // Set to pdf since they're already converted
                      solutionBlob,
                      aiOutputBlob,
                      // No diffBlob - use client-side diff computation instead
                      metadata,
                    };
                  } else {
                    // Original behavior for videos and single-file previews
                    const sideBySidePath = paths[0];
                    const videoUrl = `/solutions/${solutionId}/comparisons/${problemRunId}/${sideBySidePath}`;
                    console.log('[VideoComparisonModal] Loading side-by-side video:', videoUrl);

                    // Fetch the side-by-side video
                    const videoResponse = await apiClient.get(videoUrl, {
                      responseType: 'blob',
                    });

                    const videoBlob = videoResponse.data as Blob;
                    const videoFilename = sideBySidePath.split('/').pop() || 'side_by_side.mp4';

                    // Create metadata object from manifest data
                    const metadata: ApiVideoComparisonPair = {
                      filename: file.path,
                      solution: {
                        filename: file.path,
                        url: videoUrl,
                        size_bytes: file.left_metadata?.size_bytes || videoBlob.size,
                        mime_type: file.left_metadata?.mime_type || 'video/mp4',
                        duration_seconds: file.left_metadata?.duration_seconds || null,
                        frame_count: file.left_metadata?.frame_count || null,
                        frame_rate: file.left_metadata?.frame_rate || null,
                        width: file.left_metadata?.width || null,
                        height: file.left_metadata?.height || null,
                        codec: file.left_metadata?.codec || null,
                      },
                      ai_output: {
                        filename: file.path,
                        url: videoUrl,
                        size_bytes: file.right_metadata?.size_bytes || videoBlob.size,
                        mime_type: file.right_metadata?.mime_type || 'video/mp4',
                        duration_seconds: file.right_metadata?.duration_seconds || null,
                        frame_count: file.right_metadata?.frame_count || null,
                        frame_rate: file.right_metadata?.frame_rate || null,
                        width: file.right_metadata?.width || null,
                        height: file.right_metadata?.height || null,
                        codec: file.right_metadata?.codec || null,
                      },
                    };

                    return {
                      filename: file.path,
                      solutionFilename: videoFilename,
                      fileType: 'video' as ComparisonFileType,
                      solutionBlob: videoBlob,
                      aiOutputBlob: videoBlob, // Same blob for side-by-side (single video)
                      metadata: metadata,
                    };
                  }
                }),
              );

            console.log('[VideoComparisonModal] Built pairs from manifest:', pairs.length, pairs);
            setVideoPairs(pairs);

            // Set the first pair as active and create blob URLs
            if (pairs.length > 0) {
              const firstPair = pairs[0];
              const videoFile = new File([firstPair.solutionBlob], firstPair.solutionFilename, {
                type: firstPair.solutionBlob.type || 'video/mp4',
              });

              setVideo1File(videoFile);
              setVideo2File(videoFile);

              // Create blob URLs for the files
              const solUrl = URL.createObjectURL(firstPair.solutionBlob);
              const aiUrl = URL.createObjectURL(firstPair.aiOutputBlob);

              setVideo1Url(solUrl);
              setVideo2Url(aiUrl);
              video1UrlRef.current = solUrl;
              video2UrlRef.current = aiUrl;

              // Use the correct file type from the pair
              setCurrentFileType(firstPair.fileType);

              console.log('[VideoComparisonModal] Created blob URLs for side-by-side files:', {
                solUrl,
                aiUrl,
                fileType: firstPair.fileType,
                filename: firstPair.filename,
                solutionMetadata: firstPair.metadata?.solution,
                aiMetadata: firstPair.metadata?.ai_output,
              });
            }

            setLoadingVideos(false);
            return;
            } else {
              console.log(
                '[VideoComparisonModal] No side-by-side videos found in manifest',
              );
              setLoadingError("No side-by-side preview found");
              setLoadingVideos(false);
              return;
            }
          }
        } catch (error) {
          console.error(
            '[VideoComparisonModal] Failed to fetch solution or manifest.json:',
            error,
          );
          setLoadingError("Failed to load side-by-side preview");
          setLoadingVideos(false);
          return;
        }
      } else {
        // For non-shotcut software, download artifacts directly and process client-side
        console.log("[VideoComparisonModal] Non-shotcut software, downloading artifacts directly");

        // Determine artifact filenames based on software type
        const getArtifactFilename = (sw: string) => {
          switch (sw.toLowerCase()) {
            case "audacity":
            case "gimp":
            case "libreoffice":
            case "inkscape":
              return "final_state.zip";
            case "freecad":
              return "user-problem-model-output.FCStd";
            case "blender":
              return "user-problem-model-output.blend";
            case "musescore":
              return "user-problem-model-output.mscz";
            case "kicad":
            case "immich":
            case "odoo":
            case "erpnext":
              return "user-problem-model-output.zip";
            default:
              return "final_state.zip";
          }
        };

        const aiOutputFilename = getArtifactFilename(software);

        // Download both artifacts: AI output and user-uploaded solution
        const [aiOutputResponse, solutionResponse] = await Promise.all([
          apiClient.get(`/problem-runs/${problemRunId}/artifacts/${aiOutputFilename}`, {
            responseType: 'arraybuffer',
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.total) {
                setDownloadProgress((prev) => ({
                  ...prev,
                  aiOutput: {
                    loaded: progressEvent.loaded,
                    total: progressEvent.total as number,
                  },
                }));
              }
            },
          }),
          // Download user-uploaded solution instead of creator's solution
          apiClient.get(`/solutions/${problemRun.problem_id}/download`, {
            responseType: 'arraybuffer',
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.total) {
                setDownloadProgress((prev) => ({
                  ...prev,
                  solution: {
                    loaded: progressEvent.loaded,
                    total: progressEvent.total as number,
                  },
                }));
              }
            },
          }),
        ]);

        const aiOutputBlob = new Blob([aiOutputResponse.data]);
        const solutionBlob = new Blob([solutionResponse.data]);

        // Extract solution filename from Content-Disposition header
        const contentDisposition = solutionResponse.headers['content-disposition'];
        let solutionFilename = 'solution.zip'; // default fallback
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
          if (filenameMatch && filenameMatch[1]) {
            solutionFilename = filenameMatch[1];
          }
        }

        // Try to unzip AI output - but check if it's an Office document (which is also a ZIP internally)
        let aiOutputZip: JSZip;
        try {
          const testZip = await JSZip.loadAsync(aiOutputBlob);
          const files = Object.keys(testZip.files);

          // Check if this looks like an Office document
          const isOfficeDoc = files.some(f =>
            f === '[Content_Types].xml' ||
            f.startsWith('_rels/') ||
            f.startsWith('word/') ||
            f.startsWith('ppt/') ||
            f.startsWith('xl/') ||
            f.startsWith('docProps/')
          );

          if (isOfficeDoc) {
            aiOutputZip = new JSZip();
            aiOutputZip.file(aiOutputFilename, aiOutputBlob);
          } else {
            aiOutputZip = testZip;
          }
        } catch {
          // If it's not a zip, create a virtual zip with the single file
          aiOutputZip = new JSZip();
          aiOutputZip.file(aiOutputFilename, aiOutputBlob);
        }

        // Try to unzip solution - but check if it's an Office document
        let solutionZip: JSZip;
        try {
          const testZip = await JSZip.loadAsync(solutionBlob);
          const files = Object.keys(testZip.files);

          // Check if this looks like an Office document
          const isOfficeDoc = files.some(f =>
            f === '[Content_Types].xml' ||
            f.startsWith('_rels/') ||
            f.startsWith('word/') ||
            f.startsWith('ppt/') ||
            f.startsWith('xl/') ||
            f.startsWith('docProps/') ||
            f.startsWith('META-INF/') ||
            f === 'content.xml' ||
            f === 'styles.xml' ||
            f === 'meta.xml'
          );

          if (isOfficeDoc) {
            solutionZip = new JSZip();
            solutionZip.file(solutionFilename, solutionBlob);
          } else {
            solutionZip = testZip;
          }
        } catch {
          // If it's not a zip, create a virtual zip with the single file
          solutionZip = new JSZip();
          solutionZip.file(solutionFilename, solutionBlob);
        }

        const aiOutputFiles = new Map<
          string,
          { file: JSZip.JSZipObject; info: ReturnType<typeof getFileTypeInfo> }
        >();
        const solutionFiles = new Map<
          string,
          { file: JSZip.JSZipObject; info: ReturnType<typeof getFileTypeInfo> }
        >();

        aiOutputZip.forEach((relativePath, file) => {
          if (file.dir) return;
          const filename = relativePath.split("/").pop() || relativePath;
          aiOutputFiles.set(filename, { file, info: getFileTypeInfo(filename) });
        });

        solutionZip.forEach((relativePath, file) => {
          if (file.dir) return;
          const filename = relativePath.split("/").pop() || relativePath;
          solutionFiles.set(filename, { file, info: getFileTypeInfo(filename) });
        });

        const pairs: VideoPair[] = [];
        const usedSolutionFiles = new Set<string>();

        for (const [aiFilename, aiEntry] of Array.from(aiOutputFiles.entries())) {
          let matchedSolution: {
            filename: string;
            entry: {
              file: JSZip.JSZipObject;
              info: ReturnType<typeof getFileTypeInfo>;
            };
          } | null = null;

          // Match files by exact filename (including extension)
          for (const [solutionFilename, solutionEntry] of solutionFiles.entries()) {
            if (usedSolutionFiles.has(solutionFilename)) continue;
            if (solutionFilename === aiFilename) {
              matchedSolution = {
                filename: solutionFilename,
                entry: solutionEntry,
              };
              break;
            }
          }

          if (!matchedSolution) {
            continue;
          }

          const [aiArrayBuffer, solArrayBuffer] = await Promise.all([
            aiEntry.file.async("arraybuffer"),
            matchedSolution.entry.file.async("arraybuffer"),
          ]);

          const aiBlobFile = new Blob([aiArrayBuffer], {
            type: aiEntry.info.mimeType,
          });
          const solBlobFile = new Blob([solArrayBuffer], {
            type: matchedSolution.entry.info.mimeType,
          });

          const preferredType =
            aiEntry.info.type !== "other"
              ? aiEntry.info.type
              : matchedSolution.entry.info.type;

          // Extract metadata client-side for video/audio files
          let extractedMetadata: ApiVideoComparisonPair | undefined = undefined;
          if (preferredType === "video" || preferredType === "audio") {
            try {
              const [solutionMeta, aiMeta] = await Promise.all([
                extractMediaMetadata(solBlobFile, matchedSolution.filename, preferredType),
                extractMediaMetadata(aiBlobFile, aiFilename, preferredType),
              ]);
              extractedMetadata = {
                filename: aiFilename,
                solution: solutionMeta,
                ai_output: aiMeta,
              };
            } catch (error) {
              console.error("[VideoComparisonModal] Failed to extract metadata:", error);
            }
          }

          pairs.push({
            filename: aiFilename,
            solutionFilename: matchedSolution.filename,
            fileType: preferredType,
            solutionBlob: solBlobFile,
            aiOutputBlob: aiBlobFile,
            metadata: extractedMetadata,
          });

          usedSolutionFiles.add(matchedSolution.filename);
        }

        if (pairs.length === 0) {
          setLoadingError("No matching files found in artifacts");
          setLoadingVideos(false);
          return;
        }

        setVideoPairs(pairs);
        setSelectedPairIndex(0);
        setUnsupportedCodecError(null);

        // Load the first pair automatically
        await loadVideoPair(pairs[0]);

        setLoadingVideos(false);
        return;
      }
    } catch (error: any) {
      console.error("Failed to fetch and process files:", error);

      // Check if the error is a 404 from the solution download (either from fetch or axios)
      const is404SolutionError =
        (error.status === 404 && error.url?.includes('/solutions/')) ||
        (error.response?.status === 404 && error.response?.config?.url?.includes('/solutions/') && error.response?.config?.url?.includes('/download'));

      if (is404SolutionError) {
        setLoadingError("Please upload your solution before viewing the comparison. You need to submit your solution to compare it with the AI output.");
      } else {
        setLoadingError("Failed to load files from artifacts");
      }
      setLoadingVideos(false);
    }
  }, [problemRunId, loadVideoPair, isTutorial]);

  useEffect(() => {
    if (isOpen && problemRunId) {
      void fetchAndProcessVideos();
    }
  }, [isOpen, problemRunId, fetchAndProcessVideos]);

  const handlePairSelection = (index: number) => {
    setSelectedPairIndex(index);
    void loadVideoPair(videoPairs[index]);
  };

  const handleDownloadSolution = () => {
    if (videoPairs.length === 0 || !videoPairs[selectedPairIndex]) return;

    const pair = videoPairs[selectedPairIndex];
    const url = URL.createObjectURL(pair.solutionBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = pair.solutionFilename || `solution_${pair.filename}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadAIOutput = () => {
    if (videoPairs.length === 0 || !videoPairs[selectedPairIndex]) return;

    const pair = videoPairs[selectedPairIndex];
    const url = URL.createObjectURL(pair.aiOutputBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = pair.filename || "ai_output";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const selectedPair = videoPairs[selectedPairIndex];
    const metadata = selectedPair?.metadata;
    if (
      !selectedPair ||
      (selectedPair.fileType !== "video" && selectedPair.fileType !== "audio")
    ) {
      setLengthMismatch(false);
      return;
    }
    const solutionDuration = metadata?.solution?.duration_seconds;
    const aiDuration = metadata?.ai_output?.duration_seconds;

    if (
      solutionDuration !== null &&
      solutionDuration !== undefined &&
      aiDuration !== null &&
      aiDuration !== undefined
    ) {
      const tolerance = 0.1;
      setLengthMismatch(Math.abs(solutionDuration - aiDuration) > tolerance);
      return;
    }

    if (video1Duration !== null && video2Duration !== null) {
      const tolerance = 0.1;
      setLengthMismatch(Math.abs(video1Duration - video2Duration) > tolerance);
    } else {
      setLengthMismatch(false);
    }
  }, [videoPairs, selectedPairIndex, video1Duration, video2Duration]);

  const handleVideo1Upload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("video/")) {
      setVideo1File(file);
      if (video1Url) URL.revokeObjectURL(video1Url);
      const url = URL.createObjectURL(file);
      setVideo1Url(url);
      setVideo1Duration(null);
    }
  };

  const handleVideo2Upload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("video/")) {
      setVideo2File(file);
      if (video2Url) URL.revokeObjectURL(video2Url);
      const url = URL.createObjectURL(file);
      setVideo2Url(url);
      setVideo2Duration(null);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter1 = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterVideo1Ref.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDraggingVideo1(true);
    }
  };

  const handleDragLeave1 = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterVideo1Ref.current--;
    if (dragCounterVideo1Ref.current === 0) {
      setIsDraggingVideo1(false);
    }
  };

  const handleDrop1 = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterVideo1Ref.current = 0;
    setIsDraggingVideo1(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("video/")) {
      setVideo1File(file);
      if (video1Url) URL.revokeObjectURL(video1Url);
      const url = URL.createObjectURL(file);
      setVideo1Url(url);
      setVideo1Duration(null);
    }
  };

  const handleDragEnter2 = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterVideo2Ref.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDraggingVideo2(true);
    }
  };

  const handleDragLeave2 = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterVideo2Ref.current--;
    if (dragCounterVideo2Ref.current === 0) {
      setIsDraggingVideo2(false);
    }
  };

  const handleDrop2 = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterVideo2Ref.current = 0;
    setIsDraggingVideo2(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("video/")) {
      setVideo2File(file);
      if (video2Url) URL.revokeObjectURL(video2Url);
      const url = URL.createObjectURL(file);
      setVideo2Url(url);
      setVideo2Duration(null);
    }
  };

  const loadImageElement = useCallback((src: string) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = src;
    });
  }, []);

  const handleImageMouseMove = useCallback(
    (
      type: "solution" | "ai" | "diff",
      event: React.MouseEvent<HTMLImageElement>,
    ) => {
      const img = event.currentTarget;
      const rect = img.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;
      const dimensions =
        type === "diff"
          ? solutionImageDimensions
          : type === "solution"
            ? solutionImageDimensions
            : aiImageDimensions;

      if (!dimensions || rect.width === 0 || rect.height === 0) {
        return;
      }

      const x = Math.min(
        dimensions.width - 1,
        Math.max(0, Math.floor((offsetX / rect.width) * dimensions.width)),
      );
      const y = Math.min(
        dimensions.height - 1,
        Math.max(0, Math.floor((offsetY / rect.height) * dimensions.height)),
      );

      const hoverState: HoverCoords = {
        x,
        y,
        offsetX: event.clientX,
        offsetY: event.clientY,
        rectWidth: rect.width,
        rectHeight: rect.height,
      };

      if (type === "solution") {
        setSolutionHoverCoords(hoverState);
      } else if (type === "ai") {
        setAiHoverCoords(hoverState);
      } else {
        setDiffHoverCoords(hoverState);
      }
    },
    [solutionImageDimensions, aiImageDimensions],
  );

  const handleVideoMouseMove = useCallback(
    (type: "solution" | "ai", event: React.MouseEvent<HTMLVideoElement>) => {
      const video = event.currentTarget;
      const rect = video.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;

      // For videos, use videoWidth and videoHeight
      const dimensions = { width: video.videoWidth, height: video.videoHeight };

      if (
        !dimensions.width ||
        !dimensions.height ||
        rect.width === 0 ||
        rect.height === 0
      ) {
        return;
      }

      const x = Math.min(
        dimensions.width - 1,
        Math.max(0, Math.floor((offsetX / rect.width) * dimensions.width)),
      );
      const y = Math.min(
        dimensions.height - 1,
        Math.max(0, Math.floor((offsetY / rect.height) * dimensions.height)),
      );

      const hoverState: HoverCoords = {
        x,
        y,
        offsetX: event.clientX,
        offsetY: event.clientY,
        rectWidth: rect.width,
        rectHeight: rect.height,
      };

      if (type === "solution") {
        setSolutionHoverCoords(hoverState);
      } else if (type === "ai") {
        setAiHoverCoords(hoverState);
      }
    },
    [],
  );

  const handleImageMouseLeave = useCallback(
    (type: "solution" | "ai" | "diff") => {
      if (type === "solution") {
        setSolutionHoverCoords(null);
      } else if (type === "ai") {
        setAiHoverCoords(null);
      } else {
        setDiffHoverCoords(null);
      }
    },
    [],
  );

  const renderHoverOverlay = (coords: HoverCoords | null) => {
    if (!coords) return null;

    return (
      <div
        className="image-hover-coords"
        style={{
          left: coords.offsetX,
          top: coords.offsetY,
          transform: "translate(10px, 10px)",
        }}
      >
        ({coords.x}, {coords.y})
      </div>
    );
  };

  const handleVideo1LoadedMetadata = () => {
    const videoElement = video1Ref.current;
    if (!videoElement) return;
    const isVideoElement = videoElement instanceof HTMLVideoElement;

    console.log("[VideoComparisonModal] Video1 metadata loaded:", {
      duration: videoElement.duration,
      videoWidth: isVideoElement ? videoElement.videoWidth : undefined,
      videoHeight: isVideoElement ? videoElement.videoHeight : undefined,
      readyState: videoElement.readyState,
    });

    if (
      isSideBySideMode &&
      isVideoElement &&
      videoElement.videoWidth > 0 &&
      videoElement.videoHeight > 0
    ) {
      setSideBySideVideoDimensions({
        width: videoElement.videoWidth,
        height: videoElement.videoHeight,
      });
    }

    setVideo1Duration(videoElement.duration);

    const audioSource =
      video1File ||
      (videoPairs.length > 0
        ? videoPairs[selectedPairIndex].solutionBlob
        : null);
    if (audioSource) {
      void (async () => {
        try {
          const { channels, channelCount, sampleRate, audioBuffer } =
            await decodeAudioSource(audioSource);
          solutionAudioDataRef.current = channels;
          solutionAudioChannelCountRef.current = channelCount;
          solutionAudioSampleRateRef.current = sampleRate;
          solutionAudioBufferRef.current = audioBuffer;
          drawWaveformFromData(
            channels,
            waveformCanvas1Ref,
            waveformImage1Ref,
            "#3b82f6",
          );
        } catch (error) {
          console.error(
            "Failed to generate waveform for solution audio:",
            error,
          );
          solutionAudioDataRef.current = null;
          solutionAudioChannelCountRef.current = null;
          solutionAudioSampleRateRef.current = null;
          solutionAudioBufferRef.current = null;
          clearWaveform(waveformCanvas1Ref, waveformImage1Ref);
        } finally {
          renderDifferenceWaveform();
          // Calculate initial dB levels at time 0
          calculateCurrentDbLevels(0);
        }
      })();
    } else {
      solutionAudioDataRef.current = null;
      solutionAudioChannelCountRef.current = null;
      solutionAudioSampleRateRef.current = null;
      solutionAudioBufferRef.current = null;
      clearWaveform(waveformCanvas1Ref, waveformImage1Ref);
      renderDifferenceWaveform();
    }
  };

  const handleVideo2LoadedMetadata = () => {
    const videoElement = video2Ref.current;
    if (!videoElement) return;
    const isVideoElement = videoElement instanceof HTMLVideoElement;

    console.log("[VideoComparisonModal] Video2 metadata loaded:", {
      duration: videoElement.duration,
      videoWidth: isVideoElement ? videoElement.videoWidth : undefined,
      videoHeight: isVideoElement ? videoElement.videoHeight : undefined,
      readyState: videoElement.readyState,
    });

    setVideo2Duration(videoElement.duration);

    const audioSource =
      video2File ||
      (videoPairs.length > 0
        ? videoPairs[selectedPairIndex].aiOutputBlob
        : null);
    if (audioSource) {
      void (async () => {
        try {
          const { channels, channelCount, sampleRate, audioBuffer } =
            await decodeAudioSource(audioSource);
          aiAudioDataRef.current = channels;
          aiAudioChannelCountRef.current = channelCount;
          aiAudioSampleRateRef.current = sampleRate;
          aiAudioBufferRef.current = audioBuffer;
          drawWaveformFromData(
            channels,
            waveformCanvas2Ref,
            waveformImage2Ref,
            "#3b82f6",
          );
        } catch (error) {
          console.error("Failed to generate waveform for AI audio:", error);
          aiAudioDataRef.current = null;
          aiAudioChannelCountRef.current = null;
          aiAudioSampleRateRef.current = null;
          aiAudioBufferRef.current = null;
          clearWaveform(waveformCanvas2Ref, waveformImage2Ref);
        } finally {
          renderDifferenceWaveform();
          // Calculate initial dB levels at time 0
          calculateCurrentDbLevels(0);
        }
      })();
    } else {
      aiAudioDataRef.current = null;
      aiAudioChannelCountRef.current = null;
      aiAudioSampleRateRef.current = null;
      aiAudioBufferRef.current = null;
      clearWaveform(waveformCanvas2Ref, waveformImage2Ref);
      renderDifferenceWaveform();
    }
  };

  useEffect(() => {
    if (!isOpen || currentFileType !== "image") {
      setImageDiffUrl(null);
      setImageDiffLoading(false);
      setImageDimensionMismatch(null);
      return;
    }

    const solutionUrl = video1UrlRef.current;
    const aiUrl = video2UrlRef.current;

    if (!solutionUrl || !aiUrl) {
      return;
    }

    let cancelled = false;
    setImageDiffLoading(true);
    setImageDiffUrl(null);
    setImageDimensionMismatch(null);

    (async () => {
      try {
        const [solutionImg, aiImg] = await Promise.all([
          loadImageElement(solutionUrl),
          loadImageElement(aiUrl),
        ]);

        if (cancelled) return;

        if (
          solutionImg.width !== aiImg.width ||
          solutionImg.height !== aiImg.height
        ) {
          setImageDimensionMismatch({
            solution: { width: solutionImg.width, height: solutionImg.height },
            ai: { width: aiImg.width, height: aiImg.height },
          });
          setImageDiffUrl(null);
          return;
        }

        // For SVGs, use a higher resolution to avoid blurry diffs
        // SVG images might report small natural dimensions, so scale up to at least 2048px
        let width = solutionImg.width;
        let height = solutionImg.height;
        const isSvg = solutionUrl.toLowerCase().endsWith('.svg') || aiUrl.toLowerCase().endsWith('.svg');
        if (isSvg) {
          const maxDim = Math.max(width, height);
          if (maxDim < 2048) {
            const scale = 2048 / maxDim;
            width = Math.round(width * scale);
            height = Math.round(height * scale);
          }
        }

        let solutionData: ImageData;
        let aiData: ImageData;

        // Try to use createImageBitmap with colorSpaceConversion: "none" for raster images
        // to get raw pixel data without gamma correction. Falls back to simple canvas
        // drawing for SVGs (which fail with createImageBitmap).
        try {
          const [solutionBitmap, aiBitmap] = await Promise.all([
            createImageBitmap(solutionImg, { colorSpaceConversion: "none" }),
            createImageBitmap(aiImg, { colorSpaceConversion: "none" }),
          ]);

          const solutionCanvas = document.createElement("canvas");
          solutionCanvas.width = width;
          solutionCanvas.height = height;
          const solutionCtx = solutionCanvas.getContext("2d", { willReadFrequently: true });
          if (!solutionCtx) throw new Error("Unable to access canvas context");
          solutionCtx.drawImage(solutionBitmap, 0, 0);

          const aiCanvas = document.createElement("canvas");
          aiCanvas.width = width;
          aiCanvas.height = height;
          const aiCtx = aiCanvas.getContext("2d", { willReadFrequently: true });
          if (!aiCtx) throw new Error("Unable to access canvas context");
          aiCtx.drawImage(aiBitmap, 0, 0);

          solutionData = solutionCtx.getImageData(0, 0, width, height);
          aiData = aiCtx.getImageData(0, 0, width, height);
        } catch (e) {
          // Fallback for SVGs or other images that don't support createImageBitmap
          // SVGs don't have gamma issues anyway, so simple canvas drawing is fine
          const solutionCanvas = document.createElement("canvas");
          solutionCanvas.width = width;
          solutionCanvas.height = height;
          const solutionCtx = solutionCanvas.getContext("2d");
          if (!solutionCtx) throw new Error("Unable to access canvas context");
          solutionCtx.drawImage(solutionImg, 0, 0, width, height);

          const aiCanvas = document.createElement("canvas");
          aiCanvas.width = width;
          aiCanvas.height = height;
          const aiCtx = aiCanvas.getContext("2d");
          if (!aiCtx) throw new Error("Unable to access canvas context");
          aiCtx.drawImage(aiImg, 0, 0, width, height);

          solutionData = solutionCtx.getImageData(0, 0, width, height);
          aiData = aiCtx.getImageData(0, 0, width, height);
        }

        const diffCanvas = document.createElement("canvas");
        diffCanvas.width = width;
        diffCanvas.height = height;
        const diffCtx = diffCanvas.getContext("2d");
        if (!diffCtx) throw new Error("Unable to access canvas context");

        const diffData = diffCtx.createImageData(width, height);
        const solPixels = solutionData.data;
        const aiPixels = aiData.data;
        const diffPixels = diffData.data;

        for (let i = 0; i < solPixels.length; i += 4) {
          const r1 = solPixels[i];
          const g1 = solPixels[i + 1];
          const b1 = solPixels[i + 2];
          const a1 = solPixels[i + 3];

          const r2 = aiPixels[i];
          const g2 = aiPixels[i + 1];
          const b2 = aiPixels[i + 2];
          const a2 = aiPixels[i + 3];

          // Continuous difference: 255 - abs(pixel1 - pixel2)
          // Identical pixels → white (255, 255, 255)
          // Slightly different → light gray/color (almost white)
          // Very different → dark/saturated color (shows the difference)
          //
          // Include alpha channel in the diff to properly detect transparency differences
          // (e.g., for SVGs with transparent backgrounds and black strokes)
          const alpha1 = a1 / 255;
          const alpha2 = a2 / 255;
          const premul_r1 = r1 * alpha1;
          const premul_g1 = g1 * alpha1;
          const premul_b1 = b1 * alpha1;
          const premul_r2 = r2 * alpha2;
          const premul_g2 = g2 * alpha2;
          const premul_b2 = b2 * alpha2;

          const alphaDiff = Math.abs(a1 - a2);
          const rDiff = Math.abs(premul_r1 - premul_r2);
          const gDiff = Math.abs(premul_g1 - premul_g2);
          const bDiff = Math.abs(premul_b1 - premul_b2);

          // Use the max of RGB diff and alpha diff to catch both color and transparency changes
          const maxDiff = Math.max(rDiff, gDiff, bDiff, alphaDiff);

          diffPixels[i] = 255 - maxDiff;
          diffPixels[i + 1] = 255 - maxDiff;
          diffPixels[i + 2] = 255 - maxDiff;
          diffPixels[i + 3] = 255;
        }

        diffCtx.putImageData(diffData, 0, 0);

        if (cancelled) return;

        setImageDimensionMismatch(null);
        setImageDiffUrl(diffCanvas.toDataURL("image/png"));
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to compute image diff:", error);
          setImageDiffUrl(null);
        }
      } finally {
        if (!cancelled) {
          setImageDiffLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    currentFileType,
    loadImageElement,
    selectedPairIndex,
    video1Url,
    video2Url,
  ]);

  // Compute PDF pixel differences page by page
  useEffect(() => {
    if (!isOpen || currentFileType !== "pdf") {
      setPdfDiffUrls([]);
      setPdfDiffLoading(false);
      setPdfDiffDetected(false);
      setPdfDiffPages([]);
      setPdfDimensionMismatch(null);
      return;
    }

    const solutionUrl = video1UrlRef.current;
    const aiUrl = video2UrlRef.current;

    if (!solutionUrl || !aiUrl) {
      console.log("[PDF Diff] Missing URLs - solution:", !!solutionUrl, "ai:", !!aiUrl);
      return;
    }

    console.log("[PDF Diff] Starting PDF diff computation");
    let cancelled = false;
    setPdfDiffLoading(true);
    setPdfDiffUrls([]);
    setPdfDiffDetected(false);
    setPdfDiffPages([]);
    setPdfDimensionMismatch(null);

    (async () => {
      try {
        console.log("[PDF Diff] Loading PDF documents...");
        // Load both PDF documents
        const [solutionDoc, aiDoc] = await Promise.all([
          pdfjs.getDocument(solutionUrl).promise,
          pdfjs.getDocument(aiUrl).promise,
        ]);

        console.log("[PDF Diff] PDFs loaded - solution:", solutionDoc.numPages, "pages, ai:", aiDoc.numPages, "pages");

        if (cancelled) return;

        const solutionNumPages = solutionDoc.numPages;
        const aiNumPages = aiDoc.numPages;

        // Get first page from each to check dimensions
        const [solutionFirstPage, aiFirstPage] = await Promise.all([
          solutionDoc.getPage(1),
          aiDoc.getPage(1),
        ]);

        if (cancelled) return;

        const solutionViewport = solutionFirstPage.getViewport({ scale: 2.0 });
        const aiViewport = aiFirstPage.getViewport({ scale: 2.0 });

        // Check if page counts or dimensions don't match
        if (
          solutionNumPages !== aiNumPages ||
          Math.abs(solutionViewport.width - aiViewport.width) > 1 ||
          Math.abs(solutionViewport.height - aiViewport.height) > 1
        ) {
          setPdfDimensionMismatch({
            solution: {
              numPages: solutionNumPages,
              width: Math.round(solutionViewport.width),
              height: Math.round(solutionViewport.height),
            },
            ai: {
              numPages: aiNumPages,
              width: Math.round(aiViewport.width),
              height: Math.round(aiViewport.height),
            },
          });
          setPdfDiffUrls([]);
          return;
        }

        const diffUrls: string[] = [];
        let diffDetected = false;
        const diffPages: number[] = [];

        // Process each page
        for (let pageNum = 1; pageNum <= solutionNumPages; pageNum++) {
          if (cancelled) return;

          const [solutionPage, aiPage] = await Promise.all([
            solutionDoc.getPage(pageNum),
            aiDoc.getPage(pageNum),
          ]);

          const viewport = solutionPage.getViewport({ scale: 2.0 });
          const width = Math.round(viewport.width);
          const height = Math.round(viewport.height);

          // Render solution page to canvas
          const solutionCanvas = document.createElement("canvas");
          solutionCanvas.width = width;
          solutionCanvas.height = height;
          const solutionCtx = solutionCanvas.getContext("2d", {
            willReadFrequently: true,
          });
          if (!solutionCtx) throw new Error("Unable to access canvas context");

          // Fill with white background to handle transparent PDFs
          solutionCtx.fillStyle = "white";
          solutionCtx.fillRect(0, 0, width, height);

          await solutionPage.render({
            canvasContext: solutionCtx,
            viewport: viewport,
            canvas: solutionCanvas,
          }).promise;

          if (cancelled) return;

          // Render AI page to canvas
          const aiCanvas = document.createElement("canvas");
          aiCanvas.width = width;
          aiCanvas.height = height;
          const aiCtx = aiCanvas.getContext("2d", { willReadFrequently: true });
          if (!aiCtx) throw new Error("Unable to access canvas context");

          // Fill with white background to handle transparent PDFs
          aiCtx.fillStyle = "white";
          aiCtx.fillRect(0, 0, width, height);

          await aiPage.render({
            canvasContext: aiCtx,
            viewport: viewport,
            canvas: aiCanvas,
          }).promise;

          if (cancelled) return;

          // Extract pixel data
          const solutionData = solutionCtx.getImageData(0, 0, width, height);
          const aiData = aiCtx.getImageData(0, 0, width, height);

          // Create diff canvas
          const diffCanvas = document.createElement("canvas");
          diffCanvas.width = width;
          diffCanvas.height = height;
          const diffCtx = diffCanvas.getContext("2d");
          if (!diffCtx) throw new Error("Unable to access canvas context");

          const diffData = diffCtx.createImageData(width, height);
          const solPixels = solutionData.data;
          const aiPixels = aiData.data;
          const diffPixels = diffData.data;

          // Compute pixel differences (same algorithm as image diff)
          let hasDiff = false;
          for (let i = 0; i < solPixels.length; i += 4) {
            const r1 = solPixels[i];
            const g1 = solPixels[i + 1];
            const b1 = solPixels[i + 2];

            const r2 = aiPixels[i];
            const g2 = aiPixels[i + 1];
            const b2 = aiPixels[i + 2];

            // Continuous difference: 255 - abs(pixel1 - pixel2)
            // Identical pixels → white (255, 255, 255)
            // Slightly different → light gray/color (almost white)
            // Very different → dark/saturated color (shows the difference)
            const diffR = 255 - Math.abs(r1 - r2);
            const diffG = 255 - Math.abs(g1 - g2);
            const diffB = 255 - Math.abs(b1 - b2);

            diffPixels[i] = diffR;
            diffPixels[i + 1] = diffG;
            diffPixels[i + 2] = diffB;
            diffPixels[i + 3] = 255;

            // Check if this pixel is not white (indicating a difference)
            if (diffR !== 255 || diffG !== 255 || diffB !== 255) {
              hasDiff = true;
            }
          }

          diffCtx.putImageData(diffData, 0, 0);

          if (cancelled) return;

          diffUrls.push(diffCanvas.toDataURL("image/png"));

          // If this page has differences, record it
          if (hasDiff) {
            diffDetected = true;
            diffPages.push(pageNum);
          }
        }

        if (cancelled) return;

        console.log("[PDF Diff] Computation complete! Generated", diffUrls.length, "diff images, differences found on pages:", diffPages);
        setPdfDimensionMismatch(null);
        setPdfDiffUrls(diffUrls);
        setPdfDiffDetected(diffDetected);
        setPdfDiffPages(diffPages);
      } catch (error) {
        if (!cancelled) {
          console.error("[PDF Diff] Failed to compute PDF diff:", error);
          setPdfDiffUrls([]);
        }
      } finally {
        if (!cancelled) {
          console.log("[PDF Diff] Setting loading to false");
          setPdfDiffLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, currentFileType, selectedPairIndex, video1Url, video2Url]);

  // Render audio difference waveform when both audio data are ready
  useEffect(() => {
    if (currentFileType !== "audio") return;

    const solutionChannels = solutionAudioDataRef.current;
    const aiChannels = aiAudioDataRef.current;

    // Only render if both audios are loaded
    if (solutionChannels && aiChannels) {
      renderDifferenceWaveform();
    }
  }, [
    currentFileType,
    // We can't directly depend on ref.current values, but these state changes
    // trigger when audio loads, so we use them as proxies
    video1Duration,
    video2Duration,
    renderDifferenceWaveform,
  ]);

  const handleVideo1Error = () => {
    const videoElement = video1Ref.current;
    if (!videoElement) return;
    const errorCodes = {
      1: "MEDIA_ERR_ABORTED",
      2: "MEDIA_ERR_NETWORK",
      3: "MEDIA_ERR_DECODE",
      4: "MEDIA_ERR_SRC_NOT_SUPPORTED",
    };
    console.error("[VideoComparisonModal] Video1 error:", {
      error: videoElement.error,
      errorCode: videoElement.error?.code,
      errorName:
        errorCodes[videoElement.error?.code as keyof typeof errorCodes],
      errorMessage: videoElement.error?.message,
      networkState: videoElement.networkState,
      readyState: videoElement.readyState,
      src: videoElement.src,
    });

    // For audio files that the browser can't play (like 64-bit float WAV),
    // still try to decode them for waveform analysis
    const audioSource =
      video1File ||
      (videoPairs.length > 0
        ? videoPairs[selectedPairIndex].solutionBlob
        : null);
    if (audioSource && videoElement.error?.code === 4) {
      console.log("[handleVideo1Error] Attempting to decode audio despite playback error...");
      setIsAudioPlayable(false); // Mark audio as unplayable
      void (async () => {
        try {
          const { channels, channelCount, sampleRate, audioBuffer, duration } =
            await decodeAudioSource(audioSource);
          solutionAudioDataRef.current = channels;
          solutionAudioChannelCountRef.current = channelCount;
          solutionAudioSampleRateRef.current = sampleRate;
          solutionAudioBufferRef.current = audioBuffer;

          // Set duration so the red line indicator works
          setVideo1Duration(duration);

          drawWaveformFromData(
            channels,
            waveformCanvas1Ref,
            waveformImage1Ref,
            "#3b82f6",
          );
        } catch (error) {
          console.error(
            "Failed to generate waveform for solution audio:",
            error,
          );
          solutionAudioDataRef.current = null;
          solutionAudioChannelCountRef.current = null;
          solutionAudioSampleRateRef.current = null;
          solutionAudioBufferRef.current = null;
          clearWaveform(waveformCanvas1Ref, waveformImage1Ref);
        } finally {
          renderDifferenceWaveform();
          // Calculate initial dB levels at time 0
          calculateCurrentDbLevels(0);
        }
      })();
    }
  };

  const handleVideo2Error = () => {
    const videoElement = video2Ref.current;
    if (!videoElement) return;
    const errorCodes = {
      1: "MEDIA_ERR_ABORTED",
      2: "MEDIA_ERR_NETWORK",
      3: "MEDIA_ERR_DECODE",
      4: "MEDIA_ERR_SRC_NOT_SUPPORTED",
    };
    console.error("[VideoComparisonModal] Video2 error:", {
      error: videoElement.error,
      errorCode: videoElement.error?.code,
      errorName:
        errorCodes[videoElement.error?.code as keyof typeof errorCodes],
      errorMessage: videoElement.error?.message,
      networkState: videoElement.networkState,
      readyState: videoElement.readyState,
      src: videoElement.src,
    });

    // For audio files that the browser can't play (like 64-bit float WAV),
    // still try to decode them for waveform analysis
    const audioSource =
      video2File ||
      (videoPairs.length > 0
        ? videoPairs[selectedPairIndex].aiOutputBlob
        : null);
    if (audioSource && videoElement.error?.code === 4) {
      console.log("[handleVideo2Error] Attempting to decode audio despite playback error...");
      setIsAudioPlayable(false); // Mark audio as unplayable
      void (async () => {
        try {
          const { channels, channelCount, sampleRate, audioBuffer, duration } =
            await decodeAudioSource(audioSource);
          aiAudioDataRef.current = channels;
          aiAudioChannelCountRef.current = channelCount;
          aiAudioSampleRateRef.current = sampleRate;
          aiAudioBufferRef.current = audioBuffer;

          // Set duration so the red line indicator works
          setVideo2Duration(duration);

          drawWaveformFromData(
            channels,
            waveformCanvas2Ref,
            waveformImage2Ref,
            "#3b82f6",
          );
        } catch (error) {
          console.error(
            "Failed to generate waveform for AI audio:",
            error,
          );
          aiAudioDataRef.current = null;
          aiAudioChannelCountRef.current = null;
          aiAudioSampleRateRef.current = null;
          aiAudioBufferRef.current = null;
          clearWaveform(waveformCanvas2Ref, waveformImage2Ref);
        } finally {
          renderDifferenceWaveform();
          // Calculate initial dB levels at time 0
          calculateCurrentDbLevels(0);
        }
      })();
    }
  };

  const updateWaveformIndicator = (
    canvasRef: React.RefObject<HTMLCanvasElement | null>,
    imageDataRef: React.MutableRefObject<ImageData | null>,
    currentTime: number,
    duration: number | null,
  ) => {
    const canvas = canvasRef.current;
    const imageData = imageDataRef.current;

    if (!canvas || !imageData || !duration || duration === 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Restore the original waveform
    ctx.putImageData(imageData, 0, 0);

    // Calculate position
    const progress = currentTime / duration;
    const x = Math.floor(progress * canvas.width);

    // Draw position indicator line
    ctx.strokeStyle = "#ef4444"; // Red color
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, canvas.height);
    ctx.stroke();
  };

  const stopAudioPlayback = useCallback(() => {
    // Stop any playing audio sources
    if (solutionSourceNodeRef.current) {
      try {
        solutionSourceNodeRef.current.stop();
      } catch (e) {
        // Ignore errors if already stopped
      }
      solutionSourceNodeRef.current = null;
    }
    if (aiSourceNodeRef.current) {
      try {
        aiSourceNodeRef.current.stop();
      } catch (e) {
        // Ignore errors if already stopped
      }
      aiSourceNodeRef.current = null;
    }

    // Cancel animation frame
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const startAudioPlayback = useCallback((startOffset: number) => {
    const AudioContextCtor = (window.AudioContext ||
      (window as any).webkitAudioContext) as typeof AudioContext | undefined;

    if (!AudioContextCtor) {
      console.error("Web Audio API not supported");
      return;
    }

    // Create audio context if needed
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextCtor();
    }

    const context = audioContextRef.current;

    // Resume context if suspended (required by some browsers)
    if (context.state === 'suspended') {
      void context.resume();
    }

    // Stop any existing playback
    stopAudioPlayback();

    // Create and start source nodes - play the DIFFERENCE audio
    const solutionBuffer = solutionAudioBufferRef.current;
    const aiBuffer = aiAudioBufferRef.current;

    // Create a difference buffer if both buffers exist and have same channel count
    if (solutionBuffer && aiBuffer &&
        solutionBuffer.numberOfChannels === aiBuffer.numberOfChannels) {

      // Create a buffer for the difference
      const minLength = Math.min(solutionBuffer.length, aiBuffer.length);

      // Validate that we have a valid length before creating buffer
      if (minLength <= 0) {
        console.error('[startAudioPlayback] Invalid buffer length:', minLength, 'solution:', solutionBuffer.length, 'ai:', aiBuffer.length);
        return;
      }

      const differenceBuffer = context.createBuffer(
        solutionBuffer.numberOfChannels,
        minLength,
        solutionBuffer.sampleRate
      );

      // Calculate difference for each channel
      for (let ch = 0; ch < solutionBuffer.numberOfChannels; ch++) {
        const solutionData = solutionBuffer.getChannelData(ch);
        const aiData = aiBuffer.getChannelData(ch);
        const diffData = differenceBuffer.getChannelData(ch);

        for (let i = 0; i < minLength; i++) {
          diffData[i] = solutionData[i] - aiData[i];
        }
      }

      // Play the difference
      const source = context.createBufferSource();
      source.buffer = differenceBuffer;
      source.connect(context.destination);
      source.start(0, startOffset);
      solutionSourceNodeRef.current = source;

      // Handle end of playback
      source.onended = () => {
        if (solutionSourceNodeRef.current === source) {
          setIsPlaying(false);
          stopAudioPlayback();
        }
      };
    } else if (solutionBuffer) {
      // Fallback to playing just solution if difference can't be computed
      const source = context.createBufferSource();
      source.buffer = solutionBuffer;
      source.connect(context.destination);
      source.start(0, startOffset);
      solutionSourceNodeRef.current = source;

      source.onended = () => {
        if (solutionSourceNodeRef.current === source) {
          setIsPlaying(false);
          stopAudioPlayback();
        }
      };
    }

    // Track start time for calculating current position
    audioStartTimeRef.current = context.currentTime;
    audioCurrentOffsetRef.current = startOffset;

    // Start animation loop to update current time
    const updateTime = () => {
      if (!audioContextRef.current) {
        return;
      }

      const elapsed = audioContextRef.current.currentTime - audioStartTimeRef.current;
      const newTime = audioCurrentOffsetRef.current + elapsed;

      setCurrentTime(newTime);

      // Update waveform indicators
      updateWaveformIndicator(
        waveformCanvas1Ref,
        waveformImage1Ref,
        newTime,
        video1Duration,
      );
      updateWaveformIndicator(
        waveformCanvas2Ref,
        waveformImage2Ref,
        newTime,
        video2Duration,
      );
      updateWaveformIndicator(
        waveformCanvasDiffRef,
        waveformImageDiffRef,
        newTime,
        video1Duration || video2Duration,
      );

      // Update dB levels
      if (currentFileType === "audio") {
        calculateCurrentDbLevels(newTime);
      }

      animationFrameRef.current = requestAnimationFrame(updateTime);
    };

    animationFrameRef.current = requestAnimationFrame(updateTime);
  }, [currentFileType, video1Duration, video2Duration, stopAudioPlayback, calculateCurrentDbLevels]);

  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);

    // If dragging, don't update React state to prevent re-renders that reset the slider
    if (!isSliderDraggingRef.current) {
      setCurrentTime(time);
    }

    if (currentFileType === "audio") {
      // For audio, update the offset. Only restart playback if not currently playing
      // to avoid the slider jumping back and forth
      audioCurrentOffsetRef.current = time;
      if (!isPlaying) {
        // Only update waveforms if paused
        updateWaveformIndicator(
          waveformCanvas1Ref,
          waveformImage1Ref,
          time,
          video1Duration,
        );
        updateWaveformIndicator(
          waveformCanvas2Ref,
          waveformImage2Ref,
          time,
          video2Duration,
        );
        updateWaveformIndicator(
          waveformCanvasDiffRef,
          waveformImageDiffRef,
          time,
          video1Duration || video2Duration,
        );
        calculateCurrentDbLevels(time);
      }
    } else {
      if (video1Ref.current) video1Ref.current.currentTime = time;
      if (video2Ref.current) video2Ref.current.currentTime = time;

      // Update waveform position indicators for video
      updateWaveformIndicator(
        waveformCanvas1Ref,
        waveformImage1Ref,
        time,
        video1Duration,
      );
      updateWaveformIndicator(
        waveformCanvas2Ref,
        waveformImage2Ref,
        time,
        video2Duration,
      );
      updateWaveformIndicator(
        waveformCanvasDiffRef,
        waveformImageDiffRef,
        time,
        video1Duration || video2Duration,
      );
    }
  }, [currentFileType, isPlaying, video1Duration, video2Duration, calculateCurrentDbLevels]);

  const handlePlayPause = useCallback(() => {
    setIsPlaying((prev) => {
      if (prev) {
        // Pause
        if (currentFileType === "audio") {
          stopAudioPlayback();
          // Store current time for resume
          if (audioContextRef.current) {
            const elapsed = audioContextRef.current.currentTime - audioStartTimeRef.current;
            audioCurrentOffsetRef.current = audioCurrentOffsetRef.current + elapsed;
          }
        } else {
          video1Ref.current?.pause();
          video2Ref.current?.pause();
        }
        return false;
      }

      // Play
      if (currentFileType === "audio") {
        startAudioPlayback(audioCurrentOffsetRef.current);
      } else {
        const promises: Promise<void>[] = [];
        const playVideo = (video: HTMLVideoElement | null) => {
          if (!video) return;
          const playResult = video.play();
          if (playResult && typeof playResult.then === "function") {
            promises.push(playResult.catch(() => undefined));
          }
        };

        playVideo(video1Ref.current);
        playVideo(video2Ref.current);

        if (promises.length > 0) {
          Promise.all(promises).catch(() => {
            video1Ref.current?.pause();
            video2Ref.current?.pause();
            setIsPlaying(false);
          });
        }
      }

      return true;
    });
  }, [currentFileType, startAudioPlayback, stopAudioPlayback]);

  const handleTimeUpdate = () => {
    if (video1Ref.current && !sliderRef.current?.matches(":active")) {
      const time = video1Ref.current.currentTime;
      setCurrentTime(time);

      // Update waveform position indicators
      updateWaveformIndicator(
        waveformCanvas1Ref,
        waveformImage1Ref,
        time,
        video1Duration,
      );
      updateWaveformIndicator(
        waveformCanvas2Ref,
        waveformImage2Ref,
        time,
        video2Duration,
      );
      updateWaveformIndicator(
        waveformCanvasDiffRef,
        waveformImageDiffRef,
        time,
        maxDuration || video1Duration || video2Duration,
      );

      // Update dB levels for audio files
      if (currentFileType === "audio") {
        calculateCurrentDbLevels(time);
      }
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
  };

  const formatTimeWithMs = (seconds: number | null) => {
    if (seconds === null) return "--:--.---";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    return `${mins}:${secs.toString().padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatMetadataValue = (value: any): string => {
    if (value === null || value === undefined || value === "") return "N/A";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const formatDb = (db: number): string => {
    if (!isFinite(db) || db < -100) return "-∞ dB";
    return `${db.toFixed(1)} dB`;
  };

  const renderMetadataSection = (
    metadata: any,
    isVisible: boolean,
    toggleFn: () => void,
    title: string,
  ) => {
    if (!metadata) return null;

    return (
      <div className="video-metadata-section">
        <button className="metadata-toggle" onClick={toggleFn}>
          <span>{isVisible ? "▼" : "▶"}</span>
          <span>{title}</span>
        </button>
        {isVisible && (
          <div className="metadata-content">
            <div className="metadata-group">
              <h4>Basic Video Info</h4>
              <div className="metadata-item">
                <span className="metadata-label">Codec:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.codec)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Dimensions:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.width)} ×{" "}
                  {formatMetadataValue(metadata.height)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Duration:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.duration)} seconds
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Frame Rate:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.fps)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Bitrate:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.bitrate)} kbps
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">File Size:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.file_size)} bytes
                </span>
              </div>
            </div>

            <div className="metadata-group">
              <h4>Codec Details</h4>
              <div className="metadata-item">
                <span className="metadata-label">Profile:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.codec_profile)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Level:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.codec_level)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Pixel Format:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.pix_fmt)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Color Space:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.color_space)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Color Range:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.color_range)}
                </span>
              </div>
            </div>

            <div className="metadata-group">
              <h4>Display & Format</h4>
              <div className="metadata-item">
                <span className="metadata-label">Sample Aspect Ratio:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.sample_aspect_ratio)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Display Aspect Ratio:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.display_aspect_ratio)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Rotation:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.rotation)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Field Order:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.field_order)}
                </span>
              </div>
            </div>

            <div className="metadata-group">
              <h4>Container</h4>
              <div className="metadata-item">
                <span className="metadata-label">Format:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.container_format)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Format Name:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.format_long_name)}
                </span>
              </div>
            </div>

            <div className="metadata-group">
              <h4>Audio</h4>
              <div className="metadata-item">
                <span className="metadata-label">Codec:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.audio_codec)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Sample Rate:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.audio_sample_rate)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Channels:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.audio_channels)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Bitrate:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.audio_bitrate)} kbps
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Channel Layout:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.audio_channel_layout)}
                </span>
              </div>
            </div>

            <div className="metadata-group">
              <h4>Advanced</h4>
              <div className="metadata-item">
                <span className="metadata-label">Subtitle Tracks:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.nb_subtitle_tracks)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Encoder:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.encoder)}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">HDR Metadata:</span>
                <span className="metadata-value">
                  {formatMetadataValue(metadata.hdr_metadata)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const isValidNumber = (value: number | null | undefined): value is number =>
    typeof value === "number" && Number.isFinite(value);

  const formatFrameSummary = (metadata?: ApiVideoFileDetails | null) => {
    if (!metadata) return "--";
    if (metadata.analysis_error) return metadata.analysis_error;

    const parts: string[] = [];
    if (metadata.frame_count !== null && metadata.frame_count !== undefined) {
      parts.push(
        `${metadata.frame_count.toLocaleString()} frame${metadata.frame_count === 1 ? "" : "s"}`,
      );
    }

    if (metadata.frame_rate !== null && metadata.frame_rate !== undefined) {
      const fpsValue = Number.isInteger(metadata.frame_rate)
        ? metadata.frame_rate.toFixed(0)
        : metadata.frame_rate.toFixed(2);
      parts.push(`${fpsValue}fps`);
    }

    return parts.length > 0 ? parts.join(" • ") : "--";
  };

  const formatAudioSummary = (metadata?: ApiVideoFileDetails | null) => {
    if (!metadata) return "--";
    if (metadata.analysis_error) return metadata.analysis_error;

    const parts: string[] = [];

    if (metadata.sample_rate) {
      parts.push(`${metadata.sample_rate.toLocaleString()} Hz`);
    }

    if (metadata.channels) {
      parts.push(
        `${metadata.channels} channel${metadata.channels === 1 ? "" : "s"}`,
      );
    }

    if (metadata.bit_rate) {
      const kbps = metadata.bit_rate / 1000;
      const display =
        kbps >= 100
          ? `${Math.round(kbps).toLocaleString()} kbps`
          : `${kbps.toFixed(1)} kbps`;
      parts.push(display);
    }

    if (metadata.sample_count) {
      parts.push(
        `${metadata.sample_count.toLocaleString()} sample${metadata.sample_count === 1 ? "" : "s"}`,
      );
    }

    return parts.length > 0 ? parts.join(" • ") : "--";
  };

  const getFrameNumberForTime = (
    time: number,
    metadata?: ApiVideoFileDetails | null,
  ) => {
    if (!metadata || metadata.frame_rate === null || metadata.frame_rate === undefined) return null;
    const frame = Math.floor(time * metadata.frame_rate);
    if (metadata.frame_count !== null && metadata.frame_count !== undefined && metadata.frame_count > 0) {
      return Math.min(frame, metadata.frame_count - 1);
    }
    return frame;
  };

  const getSampleNumberForTime = (
    time: number,
    metadata?: ApiVideoFileDetails | null,
  ) => {
    if (metadata?.sample_rate) {
      const sample = Math.floor(time * metadata.sample_rate);
      if (metadata.sample_count && metadata.sample_count > 0) {
        return Math.min(sample, metadata.sample_count - 1);
      }
      return sample;
    }

    const fallbackSampleRate =
      solutionAudioSampleRateRef.current ?? aiAudioSampleRateRef.current;
    if (!fallbackSampleRate) return null;

    const sample = Math.floor(time * fallbackSampleRate);
    const fallbackSampleCount =
      solutionAudioDataRef.current?.[0]?.length ??
      aiAudioDataRef.current?.[0]?.length ??
      null;

    if (fallbackSampleCount && fallbackSampleCount > 0) {
      return Math.min(sample, fallbackSampleCount - 1);
    }

    return sample;
  };

  const selectedPair = videoPairs[selectedPairIndex] ?? null;
  const isMediaPair =
    selectedPair &&
    (selectedPair.fileType === "video" || selectedPair.fileType === "audio");
  const solutionMetadata = isMediaPair
    ? (selectedPair?.metadata?.solution ?? null)
    : null;
  const aiMetadata = isMediaPair
    ? (selectedPair?.metadata?.ai_output ?? null)
    : null;

  const modalAvailableHeight =
    isSideBySideMode && modalSize.height > 0
      ? Math.max(modalSize.height - 200, 0)
      : null;
  const availableWidthForVideo =
    modalSize.width > 0
      ? Math.max(modalSize.width - CONTENT_HORIZONTAL_PADDING, 0)
      : null;
  const availableHeightForVideo =
    playerAreaHeight !== null ? playerAreaHeight : modalAvailableHeight;

  const sideBySideDisplaySize = useMemo(() => {
    if (!isSideBySideMode) return null;
    const intrinsicWidth = sideBySideVideoDimensions?.width ?? null;
    const intrinsicHeight = sideBySideVideoDimensions?.height ?? null;
    if (!intrinsicWidth || !intrinsicHeight) {
      return null;
    }

    const widthLimit = Math.min(
      availableWidthForVideo ?? Number.POSITIVE_INFINITY,
      SIDE_BY_SIDE_MAX_WIDTH,
    );
    const heightLimit = Math.min(
      availableHeightForVideo ?? Number.POSITIVE_INFINITY,
      SIDE_BY_SIDE_MAX_HEIGHT,
    );

    if (!Number.isFinite(widthLimit) || !Number.isFinite(heightLimit)) {
      return null;
    }

    const widthScale = widthLimit / intrinsicWidth;
    const heightScale = heightLimit / intrinsicHeight;
    const scale = Math.min(widthScale, heightScale);
    const safeScale = Number.isFinite(scale) && scale > 0 ? scale : 1;

    return {
      width: intrinsicWidth * safeScale,
      height: intrinsicHeight * safeScale,
    };
  }, [
    isSideBySideMode,
    sideBySideVideoDimensions,
    availableWidthForVideo,
    availableHeightForVideo,
  ]);

  const getMaxDuration = () => {
    if (!isMediaPair) {
      return 0;
    }

    const candidates = [
      video1Duration,
      video2Duration,
      solutionMetadata?.duration_seconds,
      aiMetadata?.duration_seconds,
    ].filter(isValidNumber);

    if (candidates.length === 0) {
      return 0;
    }

    return Math.max(...candidates);
  };

  const maxDuration = getMaxDuration();
  const durationsReady = maxDuration > 0;
  const clampedCurrentTime = Math.min(currentTime, maxDuration || 0);
  const isAudioPair = currentFileType === "audio";
  const frameMetadata =
    isMediaPair && !isAudioPair && solutionMetadata?.frame_rate
      ? solutionMetadata
      : isMediaPair && !isAudioPair
        ? aiMetadata
        : null;
  const audioSampleRate = isAudioPair
    ? (solutionMetadata?.sample_rate ??
      aiMetadata?.sample_rate ??
      solutionAudioSampleRateRef.current ??
      aiAudioSampleRateRef.current ??
      null)
    : null;
  const sliderStep = (() => {
    if (isAudioPair && audioSampleRate && audioSampleRate > 0) {
      return 1 / audioSampleRate;
    }
    if (!isMediaPair) {
      return 0.033333;
    }
    const fpsCandidates = [
      solutionMetadata?.frame_rate,
      aiMetadata?.frame_rate,
    ].filter((value): value is number => isValidNumber(value) && value > 0);
    if (fpsCandidates.length > 0) {
      const highestFps = Math.max(...fpsCandidates);
      return highestFps > 0 ? 1 / highestFps : 0.033333;
    }
    return 0.033333;
  })();

  const activeFileType = currentFileType;
  const solutionUrl = video1Url;
  const aiUrl = video2Url;
  const shouldShowMediaPlayers =
    activeFileType === "video" || activeFileType === "audio";
  const isDocxReady = true; // DOCX files are now converted to PDF on backend
  const isTextReady =
    activeFileType === "text"
      ? solutionTextContent !== null && aiTextContent !== null
      : true;
  const isOtherType = activeFileType === "other";
  const sideBySideContentStyle: React.CSSProperties | undefined =
    isSideBySideMode && sideBySideDisplaySize
      ? {
          width: `${sideBySideDisplaySize.width}px`,
          maxWidth: "100%",
          margin: "0 auto",
        }
      : undefined;
  const sideBySideFrameStyle: React.CSSProperties | undefined =
    isSideBySideMode && sideBySideDisplaySize
      ? {
          width: `${sideBySideDisplaySize.width}px`,
          height: `${sideBySideDisplaySize.height}px`,
          maxWidth: "100%",
          maxHeight: `${sideBySideDisplaySize.height}px`,
        }
      : undefined;
  const sideBySideVideoStyle: React.CSSProperties | undefined =
    isSideBySideMode && sideBySideDisplaySize
      ? {
          width: "100%",
          height: "100%",
          maxHeight: `${sideBySideDisplaySize.height}px`,
          display: "block",
          backgroundColor: "transparent",
          objectFit: "contain",
        }
      : undefined;

  useEffect(() => {
    if (!isOpen || !isSideBySideMode || !shouldShowMediaPlayers) {
      setPlayerAreaHeight(null);
      return;
    }

    const measure = () => {
      const playersEl = playersWrapperRef.current;
      const controlsEl = controlsRef.current;
      if (!playersEl || !controlsEl) return;
      const playersRect = playersEl.getBoundingClientRect();
      const controlsRect = controlsEl.getBoundingClientRect();
      const headerHeight =
        sideBySideHeaderRef.current?.getBoundingClientRect().height ?? 0;
      const metadataHeight =
        sideBySideMetadataRef.current?.getBoundingClientRect().height ?? 0;
      const extraSpacing = 32; // breathing room so controls never touch video
      const available = Math.max(
        controlsRect.top - playersRect.top - headerHeight - metadataHeight - extraSpacing,
        0,
      );
      setPlayerAreaHeight(available);
    };

    measure();
    window.addEventListener("resize", measure);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => measure());
      if (playersWrapperRef.current) {
        resizeObserver.observe(playersWrapperRef.current);
      }
      if (controlsRef.current) {
        resizeObserver.observe(controlsRef.current);
      }
    }

    return () => {
      window.removeEventListener("resize", measure);
      resizeObserver?.disconnect();
    };
  }, [
    isOpen,
    isSideBySideMode,
    shouldShowMediaPlayers,
    sideBySideDisplaySize?.width,
    sideBySideDisplaySize?.height,
  ]);

  useEffect(() => {
    if (!isOpen || !shouldShowMediaPlayers) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isEditableTarget =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);

      if (isEditableTarget) return;

      if (event.code === "Space" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        handlePlayPause();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        event.stopPropagation();
        // Go back one frame
        const newTime = Math.max(0, currentTime - sliderStep);
        setCurrentTime(newTime);
        if (video1Ref.current) video1Ref.current.currentTime = newTime;
        if (video2Ref.current) video2Ref.current.currentTime = newTime;
        updateWaveformIndicator(
          waveformCanvas1Ref,
          waveformImage1Ref,
          newTime,
          video1Duration,
        );
        updateWaveformIndicator(
          waveformCanvas2Ref,
          waveformImage2Ref,
          newTime,
          video2Duration,
        );
        updateWaveformIndicator(
          waveformCanvasDiffRef,
          waveformImageDiffRef,
          newTime,
          maxDuration || video1Duration || video2Duration,
        );
        if (activeFileType === "audio") {
          calculateCurrentDbLevels(newTime);
        }
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        event.stopPropagation();
        // Go forward one frame
        const newTime = Math.min(maxDuration, currentTime + sliderStep);
        setCurrentTime(newTime);
        if (video1Ref.current) video1Ref.current.currentTime = newTime;
        if (video2Ref.current) video2Ref.current.currentTime = newTime;
        updateWaveformIndicator(
          waveformCanvas1Ref,
          waveformImage1Ref,
          newTime,
          video1Duration,
        );
        updateWaveformIndicator(
          waveformCanvas2Ref,
          waveformImage2Ref,
          newTime,
          video2Duration,
        );
        updateWaveformIndicator(
          waveformCanvasDiffRef,
          waveformImageDiffRef,
          newTime,
          maxDuration || video1Duration || video2Duration,
        );
        if (activeFileType === "audio") {
          calculateCurrentDbLevels(newTime);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    isOpen,
    shouldShowMediaPlayers,
    handlePlayPause,
    currentTime,
    sliderStep,
    maxDuration,
    video1Duration,
    video2Duration,
    activeFileType,
    calculateCurrentDbLevels,
  ]);

  // Sync slider DOM value when not dragging (uncontrolled component with manual sync)
  useEffect(() => {
    if (!isSliderDraggingRef.current && sliderRef.current) {
      sliderRef.current.value = String(clampedCurrentTime);
    }
  }, [clampedCurrentTime]);

  if (!isOpen) return null;

  return (
    <div className="video-comparison-backdrop" onClick={onClose}>
      <div
        className={`video-comparison-modal${isSideBySideMode ? " side-by-side-active" : ""}`}
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="video-comparison-header">
          <h2>Compare Files Side-by-Side</h2>
          <button className="video-comparison-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="video-comparison-content">
          {loadingVideos && (
            <div className="video-loading-state">
              <div className="loading-spinner"></div>
              <p>Loading files from artifacts...</p>

              <div className="download-progress-container">
                <div className="download-item">
                  <div className="download-label">
                    AI Output (final_state.zip)
                  </div>
                  <div className="progress-bar-wrapper">
                    <div
                      className="progress-bar-fill"
                      style={{
                        width:
                          downloadProgress.aiOutput.total > 0
                            ? `${(downloadProgress.aiOutput.loaded / downloadProgress.aiOutput.total) * 100}%`
                            : "0%",
                      }}
                    />
                  </div>
                  <div className="download-stats">
                    {downloadProgress.aiOutput.total > 0 ? (
                      <>
                        {formatBytes(downloadProgress.aiOutput.loaded)} /{" "}
                        {formatBytes(downloadProgress.aiOutput.total)} (
                        {Math.round(
                          (downloadProgress.aiOutput.loaded /
                            downloadProgress.aiOutput.total) *
                            100,
                        )}
                        %)
                      </>
                    ) : (
                      formatBytes(downloadProgress.aiOutput.loaded)
                    )}
                  </div>
                </div>

                <div className="download-item">
                  <div className="download-label">Solution</div>
                  <div className="progress-bar-wrapper">
                    <div
                      className="progress-bar-fill"
                      style={{
                        width:
                          downloadProgress.solution.total > 0
                            ? `${(downloadProgress.solution.loaded / downloadProgress.solution.total) * 100}%`
                            : "0%",
                      }}
                    />
                  </div>
                  <div className="download-stats">
                    {downloadProgress.solution.total > 0 ? (
                      <>
                        {formatBytes(downloadProgress.solution.loaded)} /{" "}
                        {formatBytes(downloadProgress.solution.total)} (
                        {Math.round(
                          (downloadProgress.solution.loaded /
                            downloadProgress.solution.total) *
                            100,
                        )}
                        %)
                      </>
                    ) : (
                      formatBytes(downloadProgress.solution.loaded)
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {loadingError && (
            <div className="video-comparison-error">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{loadingError}</span>
            </div>
          )}

          {videoPairs.length > 0 && (
            <div className="video-pair-tabs">
              {videoPairs.map((pair, index) => (
                <button
                  key={pair.filename}
                  className={`video-pair-tab ${selectedPairIndex === index ? "active" : ""}`}
                  onClick={() => handlePairSelection(index)}
                >
                  {pair.filename}
                </button>
              ))}
            </div>
          )}

          {shouldShowMediaPlayers &&
            lengthMismatch &&
            (durationsReady ||
              solutionMetadata?.duration_seconds !== null ||
              aiMetadata?.duration_seconds !== null) && (
              <div className="video-comparison-warning">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>
                  Warning: Media files have different lengths! Solution:{" "}
                  {formatTimeWithMs(
                    solutionMetadata?.duration_seconds ?? video1Duration,
                  )}
                  , AI Output:{" "}
                  {formatTimeWithMs(
                    aiMetadata?.duration_seconds ?? video2Duration,
                  )}
                </span>
              </div>
            )}

          {shouldShowMediaPlayers &&
            solutionMetadata?.width &&
            aiMetadata?.width &&
            (solutionMetadata.width !== aiMetadata.width ||
              solutionMetadata.height !== aiMetadata.height) && (
              <div className="video-comparison-warning">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>
                  Warning: Videos have different dimensions! Solution:{" "}
                  {solutionMetadata.width}x{solutionMetadata.height}, AI Output:{" "}
                  {aiMetadata.width}x{aiMetadata.height}
                </span>
              </div>
            )}

          {unmatchedSolutionFiles.length > 0 && (
            <div className="video-comparison-warning">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>
                Unmatched solution files: {unmatchedSolutionFiles.join(", ")}.
              </span>
            </div>
          )}

          {shouldShowMediaPlayers && unsupportedCodecError && (
            <div className="video-comparison-error">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <div style={{ flex: 1 }}>
                <div>{unsupportedCodecError}</div>
                {videoPairs.length > 0 && (
                  <div
                    style={{ marginTop: "12px", display: "flex", gap: "12px" }}
                  >
                    <button
                      className="video-download-button"
                      onClick={handleDownloadSolution}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Download Solution
                    </button>
                    <button
                      className="video-download-button"
                      onClick={handleDownloadAIOutput}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Download AI Output
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="video-upload-section" style={{ display: "none" }}>
            <div
              className={`video-upload-box ${isDraggingVideo1 ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter1}
              onDragLeave={handleDragLeave1}
              onDrop={handleDrop1}
            >
              <label htmlFor="video1-upload">
                {video1File ? (
                  <div className="video-upload-label-content">
                    <span className="video-file-name">{video1File.name}</span>
                    <span className="video-change-text">
                      Click to change or drag & drop
                    </span>
                  </div>
                ) : (
                  <div className="video-upload-label-content">
                    <span className="video-upload-icon">📹</span>
                    <span>Upload File 1</span>
                    <span className="video-drag-hint">
                      Click or drag & drop
                    </span>
                  </div>
                )}
              </label>
              <input
                id="video1-upload"
                type="file"
                accept=".mp4,.m4v,.webm,.mov,.avi,.mkv,.flv,.wmv,.mp3,.wav,.ogg,.m4a,.flac,.aac,.pdf,.docx,.odt,.ods,.odp,.xlsx,.pptx,.txt,.csv,.json,.md,.log,.png,.jpg,.jpeg,.gif,.bmp,.svg,.xcf"
                onChange={handleVideo1Upload}
                style={{ display: "none" }}
              />
            </div>

            <div
              className={`video-upload-box ${isDraggingVideo2 ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter2}
              onDragLeave={handleDragLeave2}
              onDrop={handleDrop2}
            >
              <label htmlFor="video2-upload">
                {video2File ? (
                  <div className="video-upload-label-content">
                    <span className="video-file-name">{video2File.name}</span>
                    <span className="video-change-text">
                      Click to change or drag & drop
                    </span>
                  </div>
                ) : (
                  <div className="video-upload-label-content">
                    <span className="video-upload-icon">📹</span>
                    <span>Upload File 2</span>
                    <span className="video-drag-hint">
                      Click or drag & drop
                    </span>
                  </div>
                )}
              </label>
              <input
                id="video2-upload"
                type="file"
                accept=".mp4,.m4v,.webm,.mov,.avi,.mkv,.flv,.wmv,.mp3,.wav,.ogg,.m4a,.flac,.aac,.pdf,.docx,.odt,.ods,.odp,.xlsx,.pptx,.txt,.csv,.json,.md,.log,.png,.jpg,.jpeg,.gif,.bmp,.svg,.xcf"
                onChange={handleVideo2Upload}
                style={{ display: "none" }}
              />
            </div>
          </div>

          {currentFileType === "pdf" && pdfDimensionMismatch && (
            <div className="video-comparison-warning">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>
                PDFs have different {pdfDimensionMismatch.solution.numPages !== pdfDimensionMismatch.ai.numPages ? "page counts" : "dimensions"}.
                Solution: {pdfDimensionMismatch.solution.numPages} {pdfDimensionMismatch.solution.numPages === 1 ? "page" : "pages"} ({pdfDimensionMismatch.solution.width} × {pdfDimensionMismatch.solution.height}),
                AI Output: {pdfDimensionMismatch.ai.numPages} {pdfDimensionMismatch.ai.numPages === 1 ? "page" : "pages"} ({pdfDimensionMismatch.ai.width} × {pdfDimensionMismatch.ai.height}).
                Pixel diff is unavailable.
              </span>
            </div>
          )}

          {currentFileType === "image" && imageDimensionMismatch && (
            <div className="video-comparison-warning">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>
                Images have different dimensions. Solution:{" "}
                {imageDimensionMismatch.solution.width} ×{" "}
                {imageDimensionMismatch.solution.height}, AI Output:{" "}
                {imageDimensionMismatch.ai.width} ×{" "}
                {imageDimensionMismatch.ai.height}. Pixel diff is unavailable.
              </span>
            </div>
          )}

          {shouldShowMediaPlayers &&
            solutionUrl &&
            aiUrl &&
            !unsupportedCodecError && (
              <>
                {isSideBySideMode ? (
                  /* Side-by-side mode: Single video player with split header */
                  <div
                    className={`video-comparison-players side-by-side-mode ${activeFileType === "audio" ? "audio-mode" : ""}`}
                    ref={playersWrapperRef}
                  >
                    <div className="video-player-container single-video">
                      <div
                        className="single-video-content"
                        style={sideBySideContentStyle}
                      >
                        <div
                          className="video-header-split"
                          ref={sideBySideHeaderRef}
                        >
                          <div className="header-left">
                            <h3>Solution</h3>
                          </div>
                          <div className="header-right">
                            <h3>AI Output</h3>
                          </div>
                        </div>
                        <div
                          className="single-video-frame"
                          style={sideBySideFrameStyle}
                        >
                          <video
                            ref={video1Ref}
                            src={solutionUrl}
                            onLoadedMetadata={handleVideo1LoadedMetadata}
                            onTimeUpdate={handleTimeUpdate}
                            onEnded={handleVideoEnded}
                            onError={handleVideo1Error}
                            onLoadStart={() => {}}
                            onCanPlay={() => {}}
                            style={sideBySideVideoStyle}
                          />
                        </div>
                      </div>
                      {/* Metadata display for side-by-side mode */}
                      <div
                        className="side-by-side-metadata-container"
                        style={sideBySideContentStyle}
                        ref={sideBySideMetadataRef}
                      >
                        <div className="metadata-side solution-metadata">
                          <div className="video-info">
                            <div className="video-duration">
                              {formatTimeWithMs(
                                solutionMetadata?.duration_seconds ?? video1Duration,
                              )}
                            </div>
                            <div className="video-frames">
                              {solutionMetadata?.analysis_error ? (
                                <span className="fps-error">
                                  {solutionMetadata.analysis_error}
                                </span>
                              ) : (
                                <>
                                  {formatFrameSummary(solutionMetadata)}
                                  {solutionMetadata?.codec && (
                                    <span> • Codec: {solutionMetadata.codec}</span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="metadata-side ai-metadata">
                          <div className="video-info">
                            <div className="video-duration">
                              {formatTimeWithMs(
                                aiMetadata?.duration_seconds ?? video2Duration,
                              )}
                            </div>
                            <div className="video-frames">
                              {aiMetadata?.analysis_error ? (
                                <span className="fps-error">
                                  {aiMetadata.analysis_error}
                                </span>
                              ) : (
                                <>
                                  {formatFrameSummary(aiMetadata)}
                                  {aiMetadata?.codec && (
                                    <span> • Codec: {aiMetadata.codec}</span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Normal mode: Two separate video players */
                  <div
                    className={`video-comparison-players ${activeFileType === "audio" ? "audio-mode" : ""}`}
                  >
                    <div className="video-player-container">
                      <div className="video-header">
                        <h3>Solution</h3>
                        <button
                          className="video-download-button"
                          onClick={handleDownloadSolution}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </svg>
                          Download
                        </button>
                      </div>
                      <video
                        ref={video1Ref}
                        src={solutionUrl}
                        onLoadedMetadata={handleVideo1LoadedMetadata}
                        onTimeUpdate={handleTimeUpdate}
                        onEnded={handleVideoEnded}
                        onError={handleVideo1Error}
                        onLoadStart={() => {}}
                        onCanPlay={() => {}}
                        onMouseMove={(e) => handleVideoMouseMove("solution", e)}
                      onMouseLeave={() => handleImageMouseLeave("solution")}
                    />
                    <canvas
                      ref={waveformCanvas1Ref}
                      className="waveform-canvas"
                      width={600}
                      height={120}
                    />
                    <div className="video-info">
                      <div className="video-duration">
                        {formatTimeWithMs(
                          solutionMetadata?.duration_seconds ?? video1Duration,
                        )}
                        {isAudioPair && currentDbLevels && (
                          <span className="audio-db-inline">
                            {currentDbLevels.solution.map((db, idx) => (
                              <span key={idx} className="db-level">
                                {solutionAudioChannelCountRef.current === 2
                                  ? idx === 0
                                    ? "L"
                                    : "R"
                                  : `Ch${idx + 1}`}
                                : {formatDb(db)}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                      <div className="video-frames">
                        {solutionMetadata?.analysis_error ? (
                          <span className="fps-error">
                            {solutionMetadata.analysis_error}
                          </span>
                        ) : isAudioPair ? (
                          formatAudioSummary(solutionMetadata)
                        ) : (
                          formatFrameSummary(solutionMetadata)
                        )}
                      </div>
                    </div>
                    {renderMetadataSection(
                      solutionTranscodeMetadata,
                      showSolutionMetadata,
                      () => setShowSolutionMetadata(!showSolutionMetadata),
                      "Original Media Metadata",
                    )}
                    {/* Audio Difference Viewer */}
                    {isAudioPair && (
                      <div className="audio-diff-section">
                        <h4>Audio Difference</h4>
                        <canvas
                          ref={waveformCanvasDiffRef}
                          className="waveform-canvas waveform-diff"
                          width={600}
                          height={120}
                        />
                        {differenceStats && (
                          <div className="audio-diff-info">
                            Showing per-channel differences between solution and AI
                            output
                          </div>
                        )}
                      </div>
                    )}
                    {renderHoverOverlay(solutionHoverCoords)}
                  </div>

                  <div className="video-player-container">
                    <div className="video-header">
                      <h3>AI Output</h3>
                      <button
                        className="video-download-button"
                        onClick={handleDownloadAIOutput}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Download
                      </button>
                    </div>
                    <video
                      ref={video2Ref}
                      src={aiUrl}
                      onLoadedMetadata={handleVideo2LoadedMetadata}
                      onEnded={handleVideoEnded}
                      onError={handleVideo2Error}
                      onLoadStart={() => {}}
                      onCanPlay={() => {}}
                      onMouseMove={(e) => handleVideoMouseMove("ai", e)}
                      onMouseLeave={() => handleImageMouseLeave("ai")}
                    />
                    <canvas
                      ref={waveformCanvas2Ref}
                      className="waveform-canvas"
                      width={600}
                      height={120}
                    />
                    <div className="video-info">
                      <div className="video-duration">
                        {formatTimeWithMs(
                          aiMetadata?.duration_seconds ?? video2Duration,
                        )}
                        {isAudioPair && currentDbLevels && (
                          <span className="audio-db-inline">
                            {currentDbLevels.aiOutput.map((db, idx) => (
                              <span key={idx} className="db-level">
                                {aiAudioChannelCountRef.current === 2
                                  ? idx === 0
                                    ? "L"
                                    : "R"
                                  : `Ch${idx + 1}`}
                                : {formatDb(db)}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                      <div className="video-frames">
                        {aiMetadata?.analysis_error ? (
                          <span className="fps-error">
                            {aiMetadata.analysis_error}
                          </span>
                        ) : isAudioPair ? (
                          formatAudioSummary(aiMetadata)
                        ) : (
                          formatFrameSummary(aiMetadata)
                        )}
                      </div>
                    </div>
                    {renderMetadataSection(
                      aiTranscodeMetadata,
                      showAiMetadata,
                      () => setShowAiMetadata(!showAiMetadata),
                      "Original Media Metadata",
                    )}
                    {renderHoverOverlay(aiHoverCoords)}
                  </div>
                </div>
                )}

                <div className="video-comparison-controls" ref={controlsRef}>
                  <button
                    className="video-control-button"
                    onClick={handlePlayPause}
                    disabled={!durationsReady || (activeFileType === "audio" && !isAudioPlayable)}
                    title={activeFileType === "audio" && !isAudioPlayable ? "Audio format not supported for playback (waveform analysis only)" : ""}
                  >
                    {isPlaying ? "⏸" : "▶"}
                  </button>

                  <div className="video-time-display">
                    <div className="time-main">
                      {formatTimeWithMs(clampedCurrentTime)} /{" "}
                      {formatTimeWithMs(maxDuration)}
                    </div>
                    <div className="time-frame">
                      {(() => {
                        if (isAudioPair) {
                          const sample = getSampleNumberForTime(
                            clampedCurrentTime,
                            solutionMetadata ?? aiMetadata,
                          );
                          if (sample === null) {
                            return "Sample --";
                          }
                          const displaySample = sample + 1;
                          const maxSample =
                            solutionMetadata?.sample_count ??
                            aiMetadata?.sample_count ??
                            solutionAudioDataRef.current?.[0]?.length ??
                            aiAudioDataRef.current?.[0]?.length;
                          if (maxSample != null) {
                            const boundedSample = Math.min(
                              displaySample,
                              Math.max(maxSample, 1),
                            );
                            return `Sample ${boundedSample.toLocaleString()} of ${maxSample.toLocaleString()}`;
                          }
                          return `Sample ${displaySample.toLocaleString()}`;
                        }

                        const frame = getFrameNumberForTime(
                          clampedCurrentTime,
                          frameMetadata,
                        );
                        if (frame === null) {
                          return "Frame --";
                        }
                        const displayFrame = frame + 1;
                        const maxFrame = frameMetadata?.frame_count;
                        const boundedFrame =
                          maxFrame != null
                            ? Math.min(displayFrame, Math.max(maxFrame, 1))
                            : Math.max(displayFrame, 1);
                        return `Frame ${boundedFrame.toLocaleString()}`;
                      })()}
                    </div>
                  </div>

                  <input
                    ref={sliderRef}
                    key="audio-slider"
                    type="range"
                    min="0"
                    max={durationsReady ? maxDuration : 0}
                    step="any"
                    defaultValue={0}
                    onChange={handleSliderChange}
                    onMouseDown={() => {
                      isSliderDraggingRef.current = true;
                    }}
                    onMouseUp={() => {
                      isSliderDraggingRef.current = false;
                      // Now update React state with the final value
                      if (sliderRef.current) {
                        const finalTime = parseFloat(sliderRef.current.value);
                        setCurrentTime(finalTime);
                      }
                    }}
                    className="video-timeline-slider"
                    disabled={!durationsReady}
                  />
                </div>
              </>
            )}

          {activeFileType === "pdf" && solutionUrl && aiUrl && (
            <div className="video-comparison-players document-mode">
              <div className="video-player-container">
                <PdfViewer
                  url={solutionUrl}
                  title="Solution"
                  onDownload={handleDownloadSolution}
                />
                {/* Show pre-generated diff PDF if available, otherwise show computed pixel diff */}
                {diffPdfUrl ? (
                  <div className="pdf-diff-container">
                    <PdfViewer
                      url={diffPdfUrl}
                      title="Diff"
                      onDownload={() => {
                        if (diffPdfUrl) {
                          const link = document.createElement("a");
                          link.href = diffPdfUrl;
                          link.download = `${videoPairs[selectedPairIndex]?.filename.replace(/\.[^/.]+$/, "")}_diff.pdf`;
                          link.click();
                        }
                      }}
                    />
                  </div>
                ) : !pdfDimensionMismatch ? (
                  <div className="pdf-diff-container">
                    <div className="video-header">
                      <h3>Pixel Diff</h3>
                      {pdfDiffDetected && (
                        <div className="pdf-diff-detected-flag">
                          Diff Detected on Page{pdfDiffPages.length > 1 ? 's' : ''}: {pdfDiffPages.join(', ')}
                        </div>
                      )}
                    </div>
                    <div className="pdf-diff-scrollable">
                      {pdfDiffLoading ? (
                        <div className="video-loading-state">
                          <div className="loading-spinner"></div>
                          <p>Computing pixel differences...</p>
                        </div>
                      ) : pdfDiffUrls.length > 0 ? (
                        <div className="pdf-diff-pages">
                          {pdfDiffUrls.map((diffUrl, index) => (
                            <div key={`diff_${index + 1}`} className="pdf-diff-page-container">
                              {pdfDiffUrls.length > 1 && (
                                <div className="pdf-page-number">Page {index + 1}</div>
                              )}
                              <img
                                src={diffUrl}
                                alt={`Page ${index + 1} Pixel Diff`}
                                className="pdf-diff-page"
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="image-diff-fallback">
                          Pixel difference unavailable.
                        </p>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="video-player-container">
                <PdfViewer
                  url={aiUrl}
                  title="AI Output"
                  onDownload={handleDownloadAIOutput}
                />
              </div>
            </div>
          )}

          {activeFileType === "image" && solutionUrl && aiUrl && (
            <div className="video-comparison-players document-mode image-mode">
              <div className="video-player-container image-container">
                <div className="video-header">
                  <h3>Solution</h3>
                  <button
                    className="video-download-button"
                    onClick={handleDownloadSolution}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Download
                  </button>
                </div>
                <img
                  ref={solutionImageRef}
                  src={solutionUrl}
                  alt="Solution"
                  className="image-preview"
                  onLoad={(e) => {
                    const dims = {
                      width: e.currentTarget.naturalWidth,
                      height: e.currentTarget.naturalHeight,
                    };
                    console.log(
                      "[VideoComparisonModal] Solution image dimensions",
                      dims,
                    );
                    setSolutionImageDimensions(dims);
                  }}
                  onMouseMove={(e) => handleImageMouseMove("solution", e)}
                  onMouseLeave={() => handleImageMouseLeave("solution")}
                />
                {!imageDimensionMismatch && (
                  <div
                    className="image-diff-inline"
                    style={{ position: "relative" }}
                  >
                    <h4>Pixel Diff</h4>
                    {imageDiffLoading ? (
                      <div className="video-loading-state">
                        <div className="loading-spinner"></div>
                        <p>Computing pixel differences...</p>
                      </div>
                    ) : imageDiffUrl ? (
                      <>
                        <img
                          src={imageDiffUrl}
                          alt="Pixel Diff"
                          className="image-diff-preview"
                          onMouseMove={(e) => handleImageMouseMove("diff", e)}
                          onMouseLeave={() => handleImageMouseLeave("diff")}
                        />
                        {renderHoverOverlay(diffHoverCoords)}
                      </>
                    ) : (
                      <p className="image-diff-fallback">
                        Pixel difference unavailable.
                      </p>
                    )}
                  </div>
                )}
                {renderHoverOverlay(solutionHoverCoords)}
              </div>
              <div className="video-player-container image-container">
                <div className="video-header">
                  <h3>AI Output</h3>
                  <button
                    className="video-download-button"
                    onClick={handleDownloadAIOutput}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Download
                  </button>
                </div>
                <img
                  ref={aiImageRef}
                  src={aiUrl}
                  alt="AI Output"
                  className="image-preview"
                  onLoad={(e) => {
                    const dims = {
                      width: e.currentTarget.naturalWidth,
                      height: e.currentTarget.naturalHeight,
                    };
                    console.log(
                      "[VideoComparisonModal] AI image dimensions",
                      dims,
                    );
                    setAiImageDimensions(dims);
                  }}
                  onMouseMove={(e) => handleImageMouseMove("ai", e)}
                  onMouseLeave={() => handleImageMouseLeave("ai")}
                />
                {renderHoverOverlay(aiHoverCoords)}
              </div>
            </div>
          )}


          {activeFileType === "text" &&
            (isTextReady ? (
              <div className="video-comparison-players document-mode">
                <div className="video-player-container">
                  <div className="video-header">
                    <h3>Solution</h3>
                    <button
                      className="video-download-button"
                      onClick={handleDownloadSolution}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Download
                    </button>
                  </div>
                  <pre className="text-preview">{solutionTextContent}</pre>
                </div>
                <div className="video-player-container">
                  <div className="video-header">
                    <h3>AI Output</h3>
                    <button
                      className="video-download-button"
                      onClick={handleDownloadAIOutput}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Download
                    </button>
                  </div>
                  <pre className="text-preview">{aiTextContent}</pre>
                </div>
              </div>
            ) : (
              <div className="video-loading-state">
                <div className="loading-spinner"></div>
                <p>Preparing text preview...</p>
              </div>
            ))}

          {isOtherType && (
            <div className="video-comparison-warning">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>
                Preview not available for this file type. Use the download
                buttons to inspect the artifacts locally.
              </span>
              <div style={{ marginTop: "12px", display: "flex", gap: "12px" }}>
                <button
                  className="video-download-button"
                  onClick={handleDownloadSolution}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download Solution
                </button>
                <button
                  className="video-download-button"
                  onClick={handleDownloadAIOutput}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download AI Output
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoComparisonModal;
